<template>
  <div class="regression-library-page">
    <!-- ── 1. PROJECTS DASHBOARD VIEW ── -->
    <div v-if="currentView === 'dashboard'">
      <!-- Page Header -->
      <div class="ms-page-header mb-4">
        <div class="ms-page-header__content d-flex justify-content-between align-items-center flex-wrap gap-3 w-100">
          <div class="d-flex align-items-center gap-3">
            <div class="ms-page-header__icon">
              <i class="fa-solid fa-list-check"></i>
            </div>
            <div>
              <p class="ms-page-header__breadcrumb">Platform Delivery <span class="ms-breadcrumb-sep">/</span> Regression Library</p>
              <h1 class="ms-page-header__title">Regression Library</h1>
            </div>
          </div>
          <div>
            <button class="btn btn-info text-white btn-sm fw-bold px-3 py-2 shadow-xs" data-bs-toggle="modal" data-bs-target="#createProjectModal" @click="openCreateProjectModal">
              <i class="fa-solid fa-folder-plus me-1"></i> Create Regression Project
            </button>
          </div>
        </div>
      </div>

      <!-- Loading Projects Indicator -->
      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-info" role="status" style="width: 3rem; height: 3rem;">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="text-muted mt-3 fw-bold">Loading your regression projects dashboard...</p>
      </div>

      <!-- Projects Grid -->
      <div v-else>
        <div v-if="projects.length === 0" class="card shadow-sm border-0 bg-white p-5 text-center text-muted">
          <i class="fa-solid fa-folder-open fa-3x mb-3 text-light"></i>
          <h5 class="fw-bold">No Projects Found</h5>
          <p class="small mb-3">Create your first regression testing project to begin logging tests.</p>
          <div>
            <button class="btn btn-info text-white btn-sm fw-bold" data-bs-toggle="modal" data-bs-target="#createProjectModal" @click="openCreateProjectModal">
              <i class="fa-solid fa-plus me-1"></i> Create Project
            </button>
          </div>
        </div>

        <div v-else class="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4 mb-5">
          <!-- Project Card -->
          <div v-for="proj in projects" :key="proj.id" class="col">
            <div class="card h-100 border-0 shadow-sm project-card bg-white" @click="selectProjectView(proj)">
              <div class="card-body p-4 d-flex flex-column justify-content-between">
                <div>
                  <!-- Card Header -->
                  <div class="d-flex justify-content-between align-items-start mb-3">
                    <div class="project-icon-wrapper" :style="{ backgroundColor: isUrl(proj.icon) ? '#ffffff' : hexToRgba(proj.color || '#f59e0b', 0.08), color: proj.color || '#f59e0b', border: isUrl(proj.icon) ? '1px solid rgba(0,0,0,0.08)' : 'none' }">
                      <img v-if="isUrl(proj.icon)" :src="proj.icon" style="width: 30px; height: 30px; object-fit: contain; border-radius: 6px;" alt="" />
                      <i v-else class="fa-solid" :class="proj.icon || 'fa-folder'"></i>
                    </div>
                    <!-- Actions (Settings & Delete) -->
                    <div class="d-flex align-items-center gap-2">
                      <button class="btn btn-sm btn-link text-info border-0 p-0 card-settings-btn" @click.stop="openAndShowEditProjectModal(proj)" title="Edit Project Settings">
                        <i class="fa-solid fa-gear"></i>
                      </button>
                      <button class="btn btn-sm btn-link text-danger border-0 p-0 card-delete-btn" @click.stop="deleteProject(proj)" title="Delete Project">
                        <i class="fa-solid fa-trash-can"></i>
                      </button>
                    </div>
                  </div>

                  <!-- Title & Description -->
                  <h4 class="fw-bold text-dark mb-1 card-project-title">{{ proj.name }}</h4>
                  <p class="text-muted small text-clamp mb-4" style="min-height: 40px;">
                    {{ proj.description || 'Custom collaborative QA regression suite for the Spark testing team.' }}
                  </p>
                </div>

                <!-- Stats Progress Section -->
                <div>
                  <!-- Master Test Library Progress -->
                   <div class="mb-3">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <span class="text-muted small fw-semibold">Master Library Tests:</span>
                      <span class="fw-bold text-dark small">{{ proj.total_tests }} total</span>
                    </div>
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <span class="text-muted small fw-semibold">Total Sprints:</span>
                      <span class="fw-bold text-dark small">{{ proj.total_sprints || 0 }} runs</span>
                    </div>
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <span class="text-muted small fw-semibold">Sprints Completed:</span>
                      <span class="fw-bold text-success small">{{ proj.sprints_completed || 0 }}</span>
                    </div>
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <span class="text-muted small fw-semibold">Sprints This Year:</span>
                      <span class="fw-bold text-info small">{{ proj.sprints_this_year || 0 }}</span>
                    </div>
                  </div>

                  <!-- Active Sprint Status Section -->
                  <div class="p-3 bg-light rounded-3 mb-4" style="border: 1px dashed #dee2e6;">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span class="text-muted small fw-bold">Active Sprint:</span>
                      <span v-if="proj.has_active_sprint" class="badge bg-success bg-opacity-10 text-success fw-bold px-2 py-1" style="font-size: 11px; border: 1px solid rgba(40,167,69,0.2);">
                        <i class="fa-solid fa-bolt me-1"></i> {{ proj.active_sprint_name }}
                      </span>
                      <span v-else class="text-muted small fw-semibold text-secondary" style="font-style: italic;">no active sprint</span>
                    </div>

                    <div v-if="proj.has_active_sprint">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <span class="text-muted small fw-semibold" style="font-size: 11.5px;">Test count:</span>
                        <span class="fw-bold text-dark small" style="font-size: 11.5px;">{{ proj.sprint_total_tests }} total</span>
                      </div>
                      <div v-if="proj.sprint_moved_tests > 0" class="d-flex justify-content-between align-items-center mb-2" style="margin-top: -3px;">
                        <span class="text-muted" style="font-size: 10.5px;">Breakdown:</span>
                        <span class="text-muted fw-semibold" style="font-size: 10.5px;">
                          {{ proj.sprint_total_tests - proj.sprint_moved_tests }} active <span class="text-muted mx-2">|</span>
                          <span style="color: #8b5cf6; font-weight: 700;">{{ proj.sprint_moved_tests }} moved</span>
                        </span>
                      </div>
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <span class="text-muted small fw-semibold" style="font-size: 11.5px;">Progress:</span>
                        <span class="fw-bold text-success small" style="font-size: 11.5px;">
                          {{ (proj.sprint_total_tests - proj.sprint_moved_tests) > 0 ? Math.round((proj.sprint_passed_tests / (proj.sprint_total_tests - proj.sprint_moved_tests)) * 100) : 0 }}%
                        </span>
                      </div>
                      <div class="progress" style="height: 6px; border-radius: 3px;">
                        <div class="progress-bar bg-success" 
                             role="progressbar" 
                             :style="{ width: ((proj.sprint_total_tests - proj.sprint_moved_tests) > 0 ? (proj.sprint_passed_tests / (proj.sprint_total_tests - proj.sprint_moved_tests) * 100) : 0) + '%' }">
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center py-2 text-muted" style="font-size: 12px; font-style: italic;">
                      <i class="fa-solid fa-circle-info me-1"></i> Ready to initialize a sprint run
                    </div>
                  </div>

                  <!-- Footer link action -->
                  <div class="d-flex align-items-center justify-content-between pt-3 border-top border-light mt-2 text-info fw-bold small card-action-link">
                    <span>Open Regression Suite</span>
                    <i class="fa-solid fa-arrow-right-long card-arrow"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── 2. INDIVIDUAL PROJECT / DETAILED REGRESSION TESTS VIEW ── -->
    <div v-else-if="currentView === 'project'">
      <!-- Page Header -->
      <div class="ms-page-header mb-4">
        <div class="ms-page-header__content d-flex justify-content-between align-items-center flex-wrap gap-3 w-100">
          <div class="d-flex align-items-center gap-3">
            <!-- Back to Dashboard Trigger -->
            <button @click="backToDashboard" class="btn btn-outline-info btn-sm fw-bold px-3 py-2 shadow-xs back-btn">
              <i class="fa-solid fa-arrow-left me-1"></i> Back to Projects
            </button>
            <div class="border-start ps-3">
              <p class="ms-page-header__breadcrumb">
                Platform Delivery <span class="ms-breadcrumb-sep">/</span> Regression Library <span class="ms-breadcrumb-sep">/</span> {{ activeProject ? activeProject.name : 'Project' }}
              </p>
              
              <!-- Project Switcher & Header Controls -->
              <div class="d-flex align-items-center gap-2 mt-1 flex-wrap">
                <div class="dropdown me-2">
                  <button class="btn btn-light border dropdown-toggle fw-bold d-flex align-items-center gap-2 px-3 shadow-xs" type="button" id="projectSelectDropdown" data-bs-toggle="dropdown" aria-expanded="false" style="min-width: 250px; background: white;">
                    <img v-if="activeProject && isUrl(activeProject.icon)" :src="activeProject.icon" style="width: 20px; height: 20px; object-fit: contain; border-radius: 4px;" alt="" />
                    <i v-else class="fa-solid" :class="activeProject ? (activeProject.icon || 'fa-folder-open') : 'fa-folder-open'" :style="{ color: activeProject ? (activeProject.color || '#f59e0b') : '#f59e0b' }"></i>
                    <span>{{ activeProject ? activeProject.name : 'Select Project...' }}</span>
                  </button>
                  <ul class="dropdown-menu shadow border-0 p-1" aria-labelledby="projectSelectDropdown" style="max-height: 300px; overflow-y: auto; background-color: #ffffff !important; z-index: 1060 !important;">
                    <li v-for="proj in projects" :key="proj.id">
                      <a class="dropdown-item d-flex justify-content-between align-items-center py-2 rounded-2" href="#" @click.prevent="selectProject(proj)">
                        <div class="d-flex align-items-center gap-2">
                            <img v-if="isUrl(proj.icon)" :src="proj.icon" style="width: 18px; height: 18px; object-fit: contain; border-radius: 4px;" alt="" />
                            <i v-else class="fa-solid" :class="proj.icon || 'fa-folder'" :style="{ color: proj.color || '#f59e0b' }"></i>
                            <span :class="{ 'fw-bold text-dark': activeProjectId === proj.id }">{{ proj.name }}</span>
                        </div>
                        <div class="d-flex align-items-center gap-2">
                          <button class="btn btn-sm text-info border-0 p-0 ms-2 edit-proj-dropdown-btn" @click.stop="openAndShowEditProjectModal(proj)" title="Edit Project Settings">
                            <i class="fa-solid fa-gear small"></i>
                          </button>
                          <button class="btn btn-sm text-danger border-0 p-0 ms-1 delete-proj-btn" @click.stop="deleteProject(proj)" title="Delete Project">
                            <i class="fa-solid fa-trash-can small"></i>
                          </button>
                        </div>
                      </a>
                    </li>
                  </ul>
                </div>

                <!-- Edit Project Settings Button -->
                <button v-if="activeProject" class="btn btn-outline-secondary btn-light border fw-bold d-flex align-items-center px-3 shadow-xs me-2 project-settings-btn-main" style="background: white;" data-bs-toggle="modal" data-bs-target="#editProjectModal" @click="openEditProjectModal" title="Edit Project Settings">
                  <i class="fa-solid fa-gear project-settings-icon" style="font-size: 13px; margin-right: 8px;"></i>
                  <span class="small" style="font-size: 12.5px; font-weight: 600;">Project Settings</span>
                </button>
              </div>
            </div>
          </div>
          <!-- Export Controls Hidden per request -->
          <!--
          <div class="d-flex align-items-center gap-2">
            <button @click="exportCSV" class="btn btn-outline-success btn-sm fw-bold" :disabled="isLoading">
              <i class="fa-solid fa-file-excel me-1"></i> Export CSV
            </button>
            <button @click="downloadPDF" class="btn btn-info text-white btn-sm fw-bold" :disabled="isLoading">
              <i class="fa-solid fa-file-pdf me-1"></i> Export PDF Report
            </button>
          </div>
          -->
        </div>
      </div>

      <!-- Navigation Tabs (Agile Controls) -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="d-flex align-items-center gap-1 border-bottom pb-1 bg-white p-2 rounded-3 shadow-xs flex-wrap">
            <button class="btn nav-tab-btn" 
                    :class="{ 'active': activeTab === 'sprints' }"
                    @click="switchTab('sprints')">
              <i class="fa-solid fa-person-running me-2"></i>
              Sprint Run
            </button>
            <button class="btn nav-tab-btn" 
                    :class="{ 'active': activeTab === 'library' }"
                    @click="switchTab('library')">
              <i class="fa-solid fa-cubes-stacked me-2"></i>
              Test Library
            </button>
            <button class="btn nav-tab-btn" 
                    :class="{ 'active': activeTab === 'audit' }"
                    @click="switchTab('audit')">
              <i class="fa-solid fa-clock-rotate-left me-2"></i>
              Agile History Audit Logs
            </button>
          </div>
        </div>
      </div>

      <!-- Loading Tests Indicator -->
      <div v-if="isLoading || isLoadingSprints" class="text-center py-5">
        <div class="spinner-border text-info" role="status" style="width: 3rem; height: 3rem;">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="text-muted mt-3 fw-bold">Fetching latest project records from Spark DB...</p>
      </div>

      <div v-else>
        <!-- ── TAB 1: SPRINT RUNS (ACTIVE EXECUTION) ── -->
        <div v-if="activeTab === 'sprints'">
          <!-- Sprint Selection & Stats Bar -->
          <div class="card shadow-sm border-0 mb-4 bg-white">
            <div class="card-body p-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div class="d-flex align-items-center gap-3 flex-wrap">
                <span class="fw-bold text-muted small text-uppercase" style="font-size: 11px;">Active Sprint:</span>
                <div class="dropdown">
                  <button class="btn btn-white border dropdown-toggle fw-bold d-flex align-items-center gap-2 px-3 shadow-xs" type="button" id="sprintSelectDropdown" data-bs-toggle="dropdown" aria-expanded="false" style="background: white; min-width: 180px; height: 38px; border-radius: 8px;">
                    <i class="fa-solid fa-person-running text-info"></i>
                    <span>{{ activeSprint ? activeSprint.name : 'No Active Sprint' }}</span>
                    <span v-if="activeSprint && activeSprint.status === 'active'" class="badge bg-success bg-opacity-10 text-success text-uppercase ms-1" style="font-size: 9.5px; font-weight: 700; border-radius: 4px; padding: 2px 5px;">Active</span>
                    <span v-if="activeSprint && activeSprint.status === 'planned'" class="badge bg-secondary bg-opacity-10 text-secondary text-uppercase ms-1" style="font-size: 9.5px; font-weight: 700; border-radius: 4px; padding: 2px 5px;">Planned</span>
                    <span v-if="activeSprint && activeSprint.status === 'completed'" class="badge bg-danger bg-opacity-10 text-danger text-uppercase ms-1" style="font-size: 9.5px; font-weight: 700; border-radius: 4px; padding: 2px 5px;">Completed</span>
                  </button>
                  <ul class="dropdown-menu shadow border-0 p-1" aria-labelledby="sprintSelectDropdown" style="border-radius: 8px; background-color: #ffffff !important; z-index: 1060 !important;">
                    <li v-for="s in sprints" :key="s.id">
                      <a class="dropdown-item d-flex justify-content-between align-items-center py-2 rounded-2" href="#" @click.prevent="selectSprint(s)">
                        <div class="d-flex align-items-center gap-2">
                          <i class="fa-solid fa-person-running" 
                             :class="{
                               'text-info': s.status === 'active',
                               'text-muted': s.status === 'planned',
                               'text-danger': s.status === 'completed'
                             }"></i>
                          <span :class="{ 'fw-bold text-dark': activeSprintId === s.id }">{{ s.name }}</span>
                        </div>
                        <span class="badge rounded-pill text-uppercase" 
                              :class="{
                                'bg-success bg-opacity-10 text-success': s.status === 'active',
                                'bg-secondary bg-opacity-10 text-secondary': s.status === 'planned',
                                'bg-danger bg-opacity-10 text-danger': s.status === 'completed'
                              }" 
                              style="font-size: 10px;">{{ s.status }}</span>
                      </a>
                    </li>
                  </ul>
                </div>
                <button v-if="activeSprint && activeSprint.status === 'active'" @click="completeActiveSprint" class="btn btn-outline-danger btn-sm fw-bold px-3" style="border-radius: 8px; height: 38px;">
                  <i class="fa-solid fa-circle-check me-1"></i> Complete Sprint Run
                </button>
                <button v-if="activeSprint && activeSprint.status === 'planned'" @click="startPlannedSprint" class="btn btn-outline-success btn-sm fw-bold px-3" style="border-radius: 8px; height: 38px;">
                  <i class="fa-solid fa-play me-1"></i> Activate Sprint Run
                </button>
                <button v-if="activeSprint && activeSprint.status === 'completed'" @click="startPlannedSprint" class="btn btn-outline-warning btn-sm fw-bold px-3" style="border-radius: 8px; height: 38px;">
                  <i class="fa-solid fa-lock-open me-1"></i> Unlock & Reactivate
                </button>
              </div>
              <div class="d-flex align-items-center gap-2">
                <button class="btn btn-info text-white btn-sm fw-bold px-3" style="border-radius: 8px; height: 38px;" data-bs-toggle="modal" data-bs-target="#createSprintModal" @click="openCreateSprintModal">
                  <i class="fa-solid fa-folder-plus me-1"></i> Define New Sprint Run
                </button>
                <button v-if="activeSprint && (activeSprint.status === 'active' || activeSprint.status === 'planned')" class="btn btn-light border btn-sm fw-bold px-3" style="border-radius: 8px; height: 38px; background: white;" data-bs-toggle="modal" data-bs-target="#addTestModal" @click="openAddTestModal">
                  <i class="fa-solid fa-circle-plus text-info me-1"></i> Add Sprint Task
                </button>
                <button v-if="activeSprint && (activeSprint.status === 'active' || activeSprint.status === 'planned')" class="btn btn-outline-info btn-sm fw-bold px-3" style="border-radius: 8px; height: 38px; border-color: rgba(0, 130, 152, 0.3);" data-bs-toggle="modal" data-bs-target="#portTasksModal" @click="openPortTasksModal">
                  <i class="fa-solid fa-file-import me-1"></i> Port / Copy Tasks
                </button>
              </div>
            </div>
          </div>

          <!-- If No Sprints exist at all -->
          <div v-if="sprints.length === 0" class="card shadow-sm border-0 bg-white p-5 text-center text-muted my-4">
            <div class="mb-3">
              <i class="fa-solid fa-person-running fa-3x text-info bg-info bg-opacity-10 p-3 rounded-circle"></i>
            </div>
            <h5 class="fw-bold text-dark">No Testing Sprints Running</h5>
            <p class="small mb-4 text-muted mx-auto" style="max-width: 500px;">
              You have a master test case library, but no testing sprints are currently defined. To begin testing, define a new sprint run.
            </p>
            <div>
              <button class="btn btn-info text-white btn-sm fw-bold px-4 py-2" style="border-radius: 8px;" data-bs-toggle="modal" data-bs-target="#createSprintModal" @click="openCreateSprintModal">
                <i class="fa-solid fa-plus me-1"></i> Define First Sprint Run
              </button>
            </div>
          </div>

          <!-- If sprints exist but none selected/loaded -->
          <div v-else-if="!activeSprint" class="card shadow-sm border-0 bg-white p-5 text-center text-muted my-4">
            <i class="fa-solid fa-folder-open fa-3x mb-3 text-light"></i>
            <h5 class="fw-bold">No Sprint Selected</h5>
            <p class="small mb-0">Please choose an active or completed sprint from the selector above to track its execution.</p>
          </div>

          <!-- SPRINT RUN DATA VIEW -->
          <div v-else>
            <!-- Stats Panel -->
            <div class="row mb-4">
              <!-- Progress Circle -->
              <div class="col-lg-4 mb-3 mb-lg-0">
                <div class="card h-100 shadow-sm border-0 bg-white">
                  <div class="card-body d-flex align-items-center justify-content-between gap-3">
                    <div class="chart-container" style="width: 120px; height: 120px; position: relative;">
                      <canvas id="progressDonut" width="120" height="120"></canvas>
                      <div class="donut-text">
                        <span class="donut-percentage">{{ Math.round(overallPassedPercentage) }}%</span>
                        <span class="donut-sub">Passed</span>
                      </div>
                    </div>
                    <div class="flex-grow-1 ps-2">
                      <h5 class="fw-bold text-dark mb-1">Sprint Overview</h5>
                      <p class="text-muted small mb-2">{{ activeSprint.name }} execution statistics</p>
                      <div class="d-flex flex-column gap-1">
                        <div class="d-flex justify-content-between text-muted small">
                          <span>Total Tasks:</span>
                          <span class="fw-bold text-dark">{{ totalTests }}</span>
                        </div>
                        <div class="d-flex justify-content-between text-muted small">
                          <span>Checked Off:</span>
                          <span class="fw-bold text-dark">{{ checkedTests }} / {{ totalTests }}</span>
                        </div>
                        <div v-if="movedTests > 0" class="d-flex justify-content-between text-muted small">
                          <span>Moved Tasks:</span>
                          <span class="fw-bold" style="color: #6d28d9;">{{ movedTests }}</span>
                        </div>
                        <div class="progress mt-1" style="height: 6px; border-radius: 4px;">
                          <div class="progress-bar bg-info" role="progressbar" :style="{ width: (totalTests > 0 ? (checkedTests / totalTests * 100) : 0) + '%' }"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Quick Metrics Cards -->
              <div class="col-lg-8">
                <div class="row h-100">
                  <!-- Passed -->
                  <div class="col-sm-3 col-6 mb-3 mb-sm-0">
                    <div class="card h-100 shadow-sm border-0 stat-card passed-border bg-white">
                      <div class="card-body p-3 d-flex flex-column justify-content-between">
                        <div class="d-flex align-items-center justify-content-between">
                          <span class="stat-title">Passed</span>
                          <div class="icon-circle bg-success bg-opacity-10 text-success">
                            <i class="fa-solid fa-circle-check"></i>
                          </div>
                        </div>
                        <div class="mt-3">
                          <h3 class="fw-bold text-success mb-0">{{ passedCount }}</h3>
                          <p class="text-muted small mb-0">{{ totalTests > 0 ? Math.round(passedCount / totalTests * 100) : 0 }}% of total</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- In Progress -->
                  <div class="col-sm-3 col-6 mb-3 mb-sm-0">
                    <div class="card h-100 shadow-sm border-0 stat-card in-progress-border bg-white">
                      <div class="card-body p-3 d-flex flex-column justify-content-between">
                        <div class="d-flex align-items-center justify-content-between">
                          <span class="stat-title">In Progress</span>
                          <div class="icon-circle bg-warning bg-opacity-10 text-warning">
                            <i class="fa-solid fa-spinner fa-spin-pulse"></i>
                          </div>
                        </div>
                        <div class="mt-3">
                          <h3 class="fw-bold text-warning mb-0">{{ inProgressCount }}</h3>
                          <p class="text-muted small mb-0">{{ totalTests > 0 ? Math.round(inProgressCount / totalTests * 100) : 0 }}% of total</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Failed -->
                  <div class="col-sm-3 col-6 mb-3 mb-sm-0">
                    <div class="card h-100 shadow-sm border-0 stat-card failed-border bg-white">
                      <div class="card-body p-3 d-flex flex-column justify-content-between">
                        <div class="d-flex align-items-center justify-content-between">
                          <span class="stat-title">Failed</span>
                          <div class="icon-circle bg-danger bg-opacity-10 text-danger">
                            <i class="fa-solid fa-triangle-exclamation"></i>
                          </div>
                        </div>
                        <div class="mt-3">
                          <h3 class="fw-bold text-danger mb-0">{{ failedCount }}</h3>
                          <p class="text-muted small mb-0">{{ totalTests > 0 ? Math.round(failedCount / totalTests * 100) : 0 }}% of total</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Not Started -->
                  <div class="col-sm-3 col-6 mb-3 mb-sm-0">
                    <div class="card h-100 shadow-sm border-0 stat-card not-started-border bg-white">
                      <div class="card-body p-3 d-flex flex-column justify-content-between">
                        <div class="d-flex align-items-center justify-content-between">
                          <span class="stat-title">Not Started</span>
                          <div class="icon-circle bg-secondary bg-opacity-10 text-secondary">
                            <i class="fa-solid fa-hourglass"></i>
                          </div>
                        </div>
                        <div class="mt-3">
                          <h3 class="fw-bold text-secondary mb-0">{{ notStartedCount }}</h3>
                          <p class="text-muted small mb-0">{{ totalTests > 0 ? Math.round(notStartedCount / totalTests * 100) : 0 }}% of total</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Search & Filters -->
            <div class="card shadow-sm border-0 mb-4 bg-white">
              <div class="card-body p-3">
                <div class="row g-2 align-items-center">
                  <div class="col-md-5">
                    <input v-model.trim="searchQuery" type="text" class="form-control form-control-sm"
                      placeholder="🔍 Search tests, URL, sheet details..."
                      style="border-radius:8px; height: 40px; font-size: 14px;" />
                  </div>
                  <div class="col-md-3">
                    <select v-model="filterTester" class="form-select bg-white" style="border-radius: 8px; height: 40px; font-size: 14px;">
                      <option value="">All Testers</option>
                      <option v-for="tester in availableTesters" :key="tester" :value="tester">{{ tester }}</option>
                    </select>
                  </div>
                  <div class="col-md-2">
                    <select v-model="filterStatus" class="form-select bg-white" style="border-radius: 8px; height: 40px; font-size: 14px;">
                      <option value="">All Statuses</option>
                      <option value="passed">Passed</option>
                      <option value="in_progress">In Progress</option>
                      <option value="failed">Failed</option>
                      <option value="not_started">Not Started</option>
                      <option value="moved">Moved</option>
                    </select>
                  </div>
                  <div class="col-md-2">
                    <select v-model="filterChecked" class="form-select bg-white" style="border-radius: 8px; height: 40px; font-size: 14px;">
                      <option value="">Checked State</option>
                      <option value="checked">Checked Off Only</option>
                      <option value="unchecked">Unchecked Only</option>
                    </select>
                  </div>
                </div>

                <hr class="my-3 opacity-10" style="border-color: rgba(0, 0, 0, 0.15) !important;">

                <div class="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div class="d-flex align-items-center gap-2">
                    <span class="text-muted small fw-bold text-uppercase me-1" style="font-size: 11px; letter-spacing: 0.5px; font-family: 'Inter', sans-serif;">View Mode:</span>
                    <div class="btn-group p-0.5 bg-secondary bg-opacity-10 rounded-3">
                      <button @click="viewMode = 'table'" class="btn btn-sm px-3 fw-bold rounded-2 transition-all" :class="viewMode === 'table' ? 'btn-white shadow-sm text-info bg-white' : 'btn-link text-muted text-decoration-none border-0'">
                        <i class="fa-solid fa-table-list me-1"></i> Table
                      </button>
                      <button @click="viewMode = 'board'" class="btn btn-sm px-3 fw-bold rounded-2 transition-all" :class="viewMode === 'board' ? 'btn-white shadow-sm text-info bg-white' : 'btn-link text-muted text-decoration-none border-0'">
                        <i class="fa-solid fa-columns me-1"></i> Kanban Board
                      </button>
                    </div>
                  </div>
                  <div class="d-flex align-items-center gap-3">
                    <div v-if="currentUser" class="form-check form-switch d-flex align-items-center gap-2 m-0 p-0">
                      <label class="form-check-label small fw-bold text-muted cursor-pointer" for="assignedToMeSwitchSprints">
                        <i class="fa-solid fa-user-check text-info me-1"></i> Assigned to Me
                      </label>
                      <input v-model="filterAssignedToMe" class="form-check-input ms-0 cursor-pointer" type="checkbox" id="assignedToMeSwitchSprints" style="width: 38px; height: 19px;">
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Categories / Tables (Sprint tests) -->
            <div v-if="viewMode === 'table'" class="col-12 d-flex flex-column gap-3 mb-5">
              <div v-if="filteredGroups.length === 0" class="card shadow-sm border-0 bg-white p-5 text-center text-muted">
                <i class="fa-solid fa-list-check fa-3x mb-3 text-light"></i>
                <h5 class="fw-bold">No Tasks Found</h5>
                <p class="small mb-0">Try adding a new sprint task or adjusting filters.</p>
              </div>

              <div v-else v-for="group in filteredGroups" :key="group.id" class="card shadow-sm border-0 bg-white group-container">
                <div @click="toggleGroup(group.id)" class="card-header bg-white border-bottom-0 p-3 d-flex justify-content-between align-items-center flex-wrap gap-2 group-header" style="cursor: pointer;">
                  <div class="d-flex align-items-center gap-3">
                    <span class="collapse-icon text-muted">
                      <i class="fa-solid" :class="collapsedGroups[group.id] ? 'fa-chevron-right' : 'fa-chevron-down'"></i>
                    </span>
                    <h5 class="fw-bold mb-0 text-dark">{{ group.name }}</h5>
                  </div>
                  <div class="d-flex align-items-center gap-2 flex-wrap">
                    <!-- Status Count Metrics Pills -->
                    <!-- Passed -->
                    <span v-if="getPassedCountForGroup(group) > 0" class="badge bg-success bg-opacity-10 text-success border border-success border-opacity-20 px-2 py-1.5" style="font-size: 11px; font-weight: 600; border-radius: 6px;">
                      <i class="fa-solid fa-check me-1"></i>{{ getPassedCountForGroup(group) }} Passed
                    </span>
                    
                    <!-- In Progress -->
                    <span v-if="getInProgressCountForGroup(group) > 0" class="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25 px-2 py-1.5" style="font-size: 11px; font-weight: 600; border-radius: 6px; color: #d97706 !important;">
                      <i class="fa-solid fa-spinner fa-spin me-1" style="animation-duration: 2.5s;"></i>{{ getInProgressCountForGroup(group) }} In Progress
                    </span>

                    <!-- Failed -->
                    <span v-if="getFailedCountForGroup(group) > 0" class="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-20 px-2 py-1.5" style="font-size: 11px; font-weight: 600; border-radius: 6px;">
                      <i class="fa-solid fa-xmark me-1"></i>{{ getFailedCountForGroup(group) }} Failed
                    </span>

                    <!-- Moved / Carryover -->
                    <span v-if="getMovedCountForGroup(group) > 0" class="badge px-2 py-1.5" style="font-size: 11px; font-weight: 600; border-radius: 6px; background-color: rgba(139, 92, 246, 0.1); color: #8b5cf6; border: 1px solid rgba(139, 92, 246, 0.25);">
                      <i class="fa-solid fa-right-to-bracket me-1" style="transform: rotate(90deg); font-size: 10px; color: #8b5cf6;"></i>{{ getMovedCountForGroup(group) }} Moved
                    </span>

                    <!-- Not Started / Pending -->
                    <span v-if="getNotStartedCountForGroup(group) > 0" class="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-20 px-2 py-1.5" style="font-size: 11px; font-weight: 600; border-radius: 6px;">
                      <i class="fa-solid fa-hourglass-start me-1"></i>{{ getNotStartedCountForGroup(group) }} Pending
                    </span>

                    <!-- Separator divider vertical line -->
                    <div class="vr mx-1" style="height: 18px; opacity: 0.25;"></div>

                    <!-- Progress Percentage Badge -->
                    <span class="badge bg-info bg-opacity-10 text-info border border-info border-opacity-20 px-2.5 py-1.5 fw-bold" style="font-size: 11px; border-radius: 6px;">
                      <i class="fa-solid fa-chart-line me-1"></i>{{ getCompletionPercentageForGroup(group) }}% Passed
                    </span>

                    <!-- Main overall status tag badge -->
                    <span class="badge" :class="getGroupStatusBadgeClass(group)" style="font-size: 11px; padding: 5px 10px; border-radius: 6px; font-weight: 700; text-transform: uppercase;">
                      {{ getGroupStatus(group) }}
                    </span>
                  </div>
                </div>

                <div v-show="!collapsedGroups[group.id]" class="card-body p-0 border-top">
                  <div class="table-responsive" style="overflow: visible !important;">
                    <table class="table table-hover align-middle mb-0 text-sm">
                      <thead class="table-light">
                        <tr>
                          <th style="width: 100px;" class="text-center">ID</th>
                          <th style="width: 120px;" class="text-center">Jira ID</th>
                          <th style="width: 60px;" class="text-center">Check</th>
                          <th>Test Description</th>
                          <th style="width: 150px;">Spreadsheet Link</th>
                          <th style="width: 250px;">Status / Actions</th>
                          <th style="width: 110px;" class="text-center">Assignee</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="test in group.tests" :key="test.id" :class="{ 'row-checked bg-light bg-opacity-50': test.checked }">
                          <td class="text-center fw-bold text-muted"
                              :class="{ 'id-link-cell': test.test_id && test.test_id !== 0 }"
                              @click="test.test_id && test.test_id !== 0 ? jumpToMasterTest(test.test_id, test.task_code) : null"
                              :title="test.test_id && test.test_id !== 0 ? 'Linked to Master: Click to find this test template in Master Library' : 'Shadow task: exists only in this sprint'"
                              style="font-size: 11.5px; font-family: var(--bs-font-monospace, monospace); letter-spacing: 0.3px;">
                            {{ test.task_code || '#' + (test.test_id || test.id) }}
                          </td>
                          <td class="text-center">
                            <a v-if="test.jira_id && activeProject?.jira_board_link" :href="getJiraIssueUrl(test.jira_id)" target="_blank" class="badge text-white px-2 py-1 rounded d-inline-flex align-items-center gap-1 shadow-3xs text-decoration-none" style="background-color: #0052cc; font-size: 11px; transition: transform 0.2s;" onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">
                              <i class="fa-brands fa-jira text-white" style="font-size: 10px;"></i> {{ test.jira_id }}
                            </a>
                            <span v-else-if="test.jira_id" class="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-10 px-2 py-1 rounded d-inline-flex align-items-center gap-1" style="font-size: 11px;" title="Configure JIRA Board Link in Project settings to make this key clickable">
                              <i class="fa-brands fa-jira text-muted" style="font-size: 10px;"></i> {{ test.jira_id }}
                            </span>
                            <span v-else class="text-muted small">-</span>
                          </td>
                          <td class="text-center">
                            <input type="checkbox" v-model="test.checked" @change="saveSprintTestState(test)" :disabled="activeSprint?.status === 'planned' || activeSprint?.status === 'completed'" class="form-check-input ms-checkbox">
                          </td>
                          <td>
                            <div class="d-flex align-items-center gap-2">
                              <span class="test-desc-text" :class="{ 'text-decoration-line-through text-muted': test.checked }" style="font-size: 13.5px; font-weight: 500;">{{ test.test }}</span>

                              <!-- Edit Sprint Test Case details -->
                              <button v-if="activeSprint?.status !== 'planned' && activeSprint?.status !== 'completed'" class="btn btn-sm btn-link text-info p-0 border-0 edit-row-btn ms-1" data-bs-toggle="modal" data-bs-target="#editTestModal" @click="openEditTestModal(test)" title="Edit Test Details">
                                <i class="fa-solid fa-pen-to-square" style="font-size: 11.5px;"></i>
                              </button>

                              <!-- Marker: Only on this sprint (Shadow Copy) -->
                              <span v-if="!test.test_id || test.test_id === 0" class="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25 px-2 py-0.5 rounded-pill d-flex align-items-center gap-1 shadow-xs" style="font-size: 10px; font-weight: 600;" title="Shadow Copy: This task only exists in this sprint.">
                                <i class="fa-solid fa-ghost" style="font-size: 9px;"></i> Sprint Only
                              </span>

                              <!-- Marker: Carried over / Moved from Sprint -->
                              <span v-if="test.moved_from_sprint" class="badge px-2 py-1 rounded-pill d-flex align-items-center gap-1 shadow-2xs" style="font-size: 10px; font-weight: 650; color: #6d28d9; background-color: #f3e8ff; border: 1px solid rgba(109, 40, 217, 0.2);" :title="'Carried over from: ' + test.moved_from_sprint">
                                <i class="fa-solid fa-right-to-bracket" style="font-size: 9.5px; transform: scaleX(-1); color: #7c3aed;"></i> Moved from: {{ test.moved_from_sprint }}
                              </span>

                              <!-- Quick Action: Add to Master list -->
                              <button v-if="(!test.test_id || test.test_id === 0) && activeSprint?.status !== 'planned' && activeSprint?.status !== 'completed'" @click="promoteSprintTaskToMaster(test)" class="btn btn-link text-info p-0 border-0 ms-2 small d-flex align-items-center gap-1 text-decoration-none hover-scale transition-all" style="font-size: 11px; font-weight: 700;" title="Promote to Master Test Library">
                                <i class="fa-solid fa-cloud-arrow-up" style="font-size: 11px;"></i> Add to Master
                              </button>
                            </div>
                          </td>
                          <td>
                            <a v-if="test.link" :href="test.link" target="_blank" class="btn btn-link btn-sm text-info p-0 text-decoration-none d-flex align-items-center gap-1" title="Go to Google Sheet">
                              <i class="fa-solid fa-file-arrow-up"></i>
                              <span class="small">{{ test.sheetId && test.sheetId !== '0' ? test.sheetId : 'Sheet' }}</span>
                            </a>
                            <span v-else class="text-muted small">No Link</span>
                          </td>
                          <td>
                             <div class="d-flex align-items-center gap-2">
                               <button v-if="test.status !== 'moved' && activeSprint?.status !== 'planned' && activeSprint?.status !== 'completed'" @click="quickSetSprintStatus(test, 'passed')" class="quick-status-btn pass-btn" :class="test.status === 'passed' ? 'active-pass' : 'outline-pass'" title="Quick Pass">
                                 <i class="fa-solid fa-check"></i>
                               </button>
                               <button v-if="test.status !== 'moved' && activeSprint?.status !== 'planned' && activeSprint?.status !== 'completed'" @click="quickSetSprintStatus(test, 'failed')" class="quick-status-btn fail-btn" :class="test.status === 'failed' ? 'active-fail' : 'outline-fail'" title="Quick Fail">
                                 <i class="fa-solid fa-xmark"></i>
                               </button>
 
                               <!-- Status Label for Planned/Completed Sprints -->
                               <template v-if="activeSprint?.status === 'planned' || activeSprint?.status === 'completed'">
                                 <span class="badge border py-1.5 px-3 d-inline-flex align-items-center justify-content-center" :class="getStatusSelectClass(test.status)" style="width: 110px; font-size: 11px; height: 28px; border-radius: 6px;">
                                   {{ test.status === 'not_started' ? 'Not Started' : test.status === 'in_progress' ? 'In Progress' : test.status === 'passed' ? 'Passed' : test.status === 'failed' ? 'Failed' : test.status === 'moved' ? 'Moved' : test.status }}
                                 </span>
                               </template>
                               <!-- Active Sprint Dropdown Selector -->
                               <template v-else>
                                 <select v-model="test.status" @change="saveSprintTestState(test)" class="form-select form-select-sm status-select" :class="getStatusSelectClass(test.status)" style="width: 110px; font-size: 11.5px; height: 28px;">
                                   <option value="not_started">Not Started</option>
                                   <option value="in_progress">In Progress</option>
                                   <option value="passed">Passed</option>
                                   <option value="failed">Failed</option>
                                   <option value="moved" :disabled="test.status !== 'moved'">Moved</option>
                                 </select>
                               </template>
 
                               <!-- Carryover Move Action -->
                               <button v-if="test.status !== 'moved' && activeSprint?.status !== 'planned' && activeSprint?.status !== 'completed'" @click="openMoveTaskModal(test)" data-bs-toggle="modal" data-bs-target="#moveSprintTestModal" class="quick-status-btn move-btn outline-move" title="Move/Carryover to Next Sprint">
                                 <i class="fa-solid fa-right-to-bracket" style="transform: rotate(90deg);"></i>
                               </button>
                             </div>
                           </td>
                           <td class="text-center">
                             <!-- Tester Display for Planned/Completed Sprints (Badges only) -->
                             <template v-if="activeSprint?.status === 'planned' || activeSprint?.status === 'completed'">
                               <div class="d-inline-flex align-items-center justify-content-center gap-1" style="height: 32px;">
                                 <template v-if="!test.tester || test.tester === 'Unassigned'">
                                   <div class="avatar-circle-sm" style="background-color: #e5e7eb; color: #6b7280; font-size: 10px; font-weight: 700; width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0;" title="Unassigned">
                                     <i class="fa-solid fa-user" style="font-size: 9px;"></i>
                                   </div>
                                 </template>
                                 <template v-else>
                                   <div class="d-flex align-items-center flex-shrink-0">
                                     <div v-for="(name, idx) in test.tester.split(',').map(s => s.trim()).filter(Boolean)" :key="name" 
                                          class="avatar-circle-sm border border-white text-white d-flex align-items-center justify-content-center fw-bold position-relative shadow-3xs" 
                                          :style="{ 
                                            backgroundColor: getAvatarColor(name), 
                                            zIndex: 10 - idx, 
                                            marginLeft: idx > 0 ? '-6px' : '0px',
                                            width: '22px', 
                                            height: '22px', 
                                            borderRadius: '50%',
                                            fontSize: '9px',
                                            flexShrink: 0
                                          }"
                                          :title="name">
                                       {{ getInitials(name) }}
                                     </div>
                                   </div>
                                 </template>
                               </div>
                             </template>
                             <!-- Tester Dropdown Trigger for Active Sprints -->
                             <template v-else>
                               <div class="dropdown d-inline-block">
                                 <button class="btn btn-sm btn-white border d-flex align-items-center justify-content-between p-1" type="button" :id="'testerDropSprint_' + test.id" data-bs-toggle="dropdown" aria-expanded="false" data-bs-boundary="viewport" style="width: 58px; border-radius: 8px; height: 32px; background: #fff;" :title="test.tester || 'Unassigned'">
                                   <div class="d-flex align-items-center justify-content-center gap-1 overflow-hidden w-100">
                                     <template v-if="!test.tester || test.tester === 'Unassigned'">
                                       <div class="avatar-circle-sm" style="background-color: #e5e7eb; color: #6b7280; font-size: 10px; font-weight: 700; width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                                         <i class="fa-solid fa-user" style="font-size: 9px;"></i>
                                       </div>
                                     </template>
                                     <template v-else>
                                       <div class="d-flex align-items-center flex-shrink-0">
                                         <div v-for="(name, idx) in test.tester.split(',').map(s => s.trim()).filter(Boolean)" :key="name" 
                                              class="avatar-circle-sm border border-white text-white d-flex align-items-center justify-content-center fw-bold position-relative shadow-3xs" 
                                              :style="{ 
                                                backgroundColor: getAvatarColor(name), 
                                                zIndex: 10 - idx, 
                                                marginLeft: idx > 0 ? '-6px' : '0px',
                                                width: '22px', 
                                                height: '22px', 
                                                borderRadius: '50%',
                                                fontSize: '9px',
                                                flexShrink: 0
                                              }"
                                              :title="name">
                                           {{ getInitials(name) }}
                                         </div>
                                       </div>
                                     </template>
                                     <i class="fa-solid fa-chevron-down text-muted" style="font-size: 8px; flex-shrink: 0;"></i>
                                   </div>
                                 </button>
                                 <ul class="dropdown-menu shadow border-0 p-2" :aria-labelledby="'testerDropSprint_' + test.id" style="max-height: 250px; overflow-y: auto; font-size: 13px; min-width: 200px; border-radius: 8px; background-color: #ffffff !important; z-index: 1060 !important;">
                                   <li>
                                     <div class="form-check py-1">
                                       <input class="form-check-input ms-checkbox-sm tester-check-input" type="checkbox" :checked="isTesterSelected(test, 'Unassigned')" @change="toggleTesterSprint(test, 'Unassigned')" :id="'testSprint_' + test.id + '_unassigned'">
                                       <label class="form-check-label fw-bold text-dark ms-1 cursor-pointer" :for="'testSprint_' + test.id + '_unassigned'">Unassigned</label>
                                     </div>
                                   </li>
                                   <li><hr class="dropdown-divider"></li>
                                   <li v-for="tOpt in listTestersFiltered" :key="tOpt">
                                     <div class="form-check py-1">
                                       <input class="form-check-input ms-checkbox-sm tester-check-input" type="checkbox" :checked="isTesterSelected(test, tOpt)" @change="toggleTesterSprint(test, tOpt)" :id="'testSprint_' + test.id + '_' + tOpt">
                                       <label class="form-check-label text-dark ms-1 cursor-pointer" :for="'testSprint_' + test.id + '_' + tOpt">{{ tOpt }}</label>
                                     </div>
                                   </li>
                                 </ul>
                               </div>
                             </template>
                           </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <!-- Kanban Board (Sprints) -->
            <div v-else-if="viewMode === 'board'" class="col-12">
              <div class="row g-3 mb-5">
                <div v-for="col in ['not_started', 'in_progress', 'failed', 'passed']" :key="col" class="col-md-3 col-sm-6">
                  <div class="card h-100 bg-light bg-opacity-30 border-0 shadow-sm" style="min-height: 580px; border-radius: 12px;" @dragover.prevent @drop="handleBoardDrop($event, col)">
                    <div class="card-header bg-white border-bottom-0 py-3 px-3 d-flex justify-content-between align-items-center" style="border-radius: 12px 12px 0 0;">
                      <div class="d-flex align-items-center gap-2">
                        <span class="badge rounded-pill px-2.5 py-1.5 fw-bold text-capitalize" :class="getStatusBadgeClass(col)" style="font-size: 11px;">{{ col.replace('_', ' ') }}</span>
                        <span class="badge bg-light text-muted border border-light-subtle rounded-pill" style="font-size: 10px;">{{ getBoardColumnTests(col).length }}</span>
                      </div>
                    </div>
                    <div class="card-body p-2 d-flex flex-column gap-2 overflow-y-auto" style="max-height: 72vh;">
                      <div v-for="test in getBoardColumnTests(col)" :key="test.id" 
                           class="kanban-card card border-0 shadow-2xs p-3 bg-white cursor-grab position-relative animate-hover"
                           :style="{ borderRadius: '8px', borderLeft: '3.5px solid ' + getStatusColor(col) + ' !important', transition: 'all 0.2s ease' }"
                           :draggable="activeSprint?.status !== 'planned' && activeSprint?.status !== 'completed'" @dragstart="handleBoardDragStart($event, test)">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <div class="d-flex align-items-center gap-1.5 flex-wrap">
                            <!-- Group Label Badge -->
                            <span class="badge bg-info bg-opacity-10 text-info px-2 py-1" style="font-size: 10px; max-width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ test.group_name }}</span>
                            
                            <!-- Jira Badge on Top next to Group Label -->
                            <a v-if="test.jira_id && activeProject?.jira_board_link" :href="getJiraIssueUrl(test.jira_id)" target="_blank" class="badge text-white px-1.5 py-0.5 rounded d-inline-flex align-items-center gap-1 shadow-3xs text-decoration-none animate-hover" style="background-color: #0052cc; font-size: 9.5px; font-weight: 600; transition: transform 0.2s;" onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">
                              <i class="fa-brands fa-jira text-white" style="font-size: 8.5px;"></i> {{ test.jira_id }}
                            </a>
                            <span v-else-if="test.jira_id" class="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-10 px-1.5 py-0.5 rounded d-inline-flex align-items-center gap-1" style="font-size: 9.5px; font-weight: 600;" title="Configure JIRA Board Link in Project settings to make this key clickable">
                              <i class="fa-brands fa-jira text-muted" style="font-size: 8.5px;"></i> {{ test.jira_id }}
                            </span>
                          </div>
                          
                          <!-- Top Right actions (Move & Edit!) -->
                          <div class="d-flex align-items-center gap-2">
                            <!-- Edit button (moved here from title so it doesn't touch the description text!) -->
                            <button v-if="activeSprint?.status !== 'planned' && activeSprint?.status !== 'completed'" class="btn btn-xs btn-link text-info p-0 border-0 edit-row-btn" data-bs-toggle="modal" data-bs-target="#editTestModal" @click="openEditTestModal(test)" title="Edit Test Details">
                              <i class="fa-solid fa-pen-to-square" style="font-size: 11px;"></i>
                            </button>
                            
                            <!-- Move to Next Sprint button -->
                            <button v-if="test.status !== 'moved' && activeSprint?.status !== 'planned' && activeSprint?.status !== 'completed'" @click="openMoveTaskModal(test)" data-bs-toggle="modal" data-bs-target="#moveSprintTestModal" class="btn btn-link btn-xs text-muted p-0 border-0" title="Move to Next Sprint">
                              <i class="fa-solid fa-right-to-bracket text-purple" style="transform: rotate(90deg); font-size: 11.5px;"></i>
                            </button>
                          </div>
                        </div>
                        <p class="kanban-title mb-2 text-dark" style="font-size: 13.5px; font-weight: 550; line-height: 1.45;">
                          {{ test.test }}
                        </p>
                        <div class="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-1 border-top border-light pt-2">
                          <span class="badge bg-light text-muted border border-light-subtle rounded-pill" style="font-size: 9.5px; font-weight: 550;">{{ test.tester }}</span>
                          
                          <div class="d-flex align-items-center gap-1">
                            <span v-if="!test.test_id || test.test_id === 0" class="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25 px-1.5 py-0.5 rounded-pill d-flex align-items-center gap-1 shadow-xs" style="font-size: 8.5px; font-weight: 600;" title="Shadow Copy: This task only exists in this sprint.">
                              <i class="fa-solid fa-ghost"></i> Only Sprint
                            </span>

                            <!-- Marker: Carried over / Moved from Sprint -->
                            <span v-if="test.moved_from_sprint" class="badge px-1.5 py-0.5 rounded-pill d-flex align-items-center gap-1 shadow-3xs" style="font-size: 8.5px; font-weight: 650; color: #6d28d9; background-color: #f3e8ff; border: 1px solid rgba(109, 40, 217, 0.2);" :title="'Carried over from: ' + test.moved_from_sprint">
                              <i class="fa-solid fa-right-to-bracket" style="font-size: 8px; transform: scaleX(-1); color: #7c3aed;"></i> From: {{ test.moved_from_sprint }}
                            </span>

                            <button v-if="(!test.test_id || test.test_id === 0) && activeSprint?.status !== 'planned' && activeSprint?.status !== 'completed'" @click="promoteSprintTaskToMaster(test)" class="btn btn-link text-info p-0 border-0 ms-1 small fw-bold text-decoration-none transition-all" style="font-size: 9.5px; font-weight: 700;" title="Promote to Master Test Library">
                              <i class="fa-solid fa-cloud-arrow-up"></i> +Master
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── TAB 2: MASTER TEST CASE LIBRARY ── -->
        <div v-else-if="activeTab === 'library'">
          <!-- Integrated Library Header & Filter Card -->
          <div class="card shadow-sm border-0 mb-4 bg-white" style="border-radius: 12px;">
            <div class="card-body p-4">
              <!-- Top Row: Title & Actions -->
              <div class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 pb-3 border-bottom border-light">
                <div class="d-flex align-items-center gap-3">
                  <i class="fa-solid fa-cubes-stacked text-info bg-info bg-opacity-10 p-2 rounded-3 fs-4" style="width: 42px; height: 42px; display: flex; align-items: center; justify-content: center;"></i>
                  <div>
                    <h5 class="fw-bold text-dark mb-1">Master Test Case Library</h5>
                    <p class="text-muted small mb-0">Manage the project's master compilation of tests, independent of active sprints.</p>
                  </div>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <button class="btn btn-outline-info btn-sm fw-bold px-3 shadow-xs" style="border-radius: 8px; height: 38px;" @click="triggerCSVFileInput">
                    <i class="fa-solid fa-file-import me-1"></i> Import CSV
                  </button>
                  <input ref="csvFileInput" type="file" accept=".csv" @change="handleCSVFileSelected" style="display: none;">
                  <button class="btn btn-info text-white btn-sm fw-bold px-3 shadow-xs" style="border-radius: 8px; height: 38px;" data-bs-toggle="modal" data-bs-target="#addTestModal" @click="openAddTestModal">
                    <i class="fa-solid fa-circle-plus me-1"></i> Add Custom Test Case
                  </button>
                </div>
              </div>

              <!-- Bottom Row: Dynamic Filters -->
              <div class="row g-2 align-items-center">
                <div class="col-md-9">
                  <div class="input-group border rounded-3 overflow-hidden bg-white" style="height: 40px;">
                    <span class="input-group-text border-0 bg-transparent text-muted px-3">
                      <i class="fa-solid fa-magnifying-glass fs-6"></i>
                    </span>
                    <input v-model.trim="searchQuery" type="text" class="form-control border-0 ps-1"
                      placeholder="Search master repository tests..."
                      style="font-size: 13.5px; outline: none; box-shadow: none;" />
                  </div>
                </div>
                <div class="col-md-3">
                  <select v-model="filterTester" class="form-select bg-white" style="border-radius: 8px; height: 40px; font-size: 13.5px;">
                    <option value="">All Testers (Default Assignees)</option>
                    <option v-for="tester in availableTesters" :key="tester" :value="tester">{{ tester }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- Master Test Tables -->
          <div class="col-12 d-flex flex-column gap-3 mb-5">
            <div v-if="filteredGroups.length === 0" class="card shadow-sm border-0 bg-white p-5 text-center text-muted">
              <i class="fa-solid fa-cubes-stacked fa-3x mb-3 text-light"></i>
              <h5 class="fw-bold">No Tests in Library</h5>
              <p class="small mb-0">Create your first custom test case to begin compiling your master library.</p>
            </div>

            <div v-else v-for="group in filteredGroups" :key="group.id" class="card shadow-sm border-0 bg-white group-container">
              <div @click="toggleGroup(group.id)" class="card-header bg-white border-bottom-0 p-3 d-flex justify-content-between align-items-center flex-wrap gap-2 group-header" style="cursor: pointer;">
                <div class="d-flex align-items-center gap-3">
                  <span class="collapse-icon text-muted">
                    <i class="fa-solid" :class="collapsedGroups[group.id] ? 'fa-chevron-right' : 'fa-chevron-down'"></i>
                  </span>
                  <h5 class="fw-bold mb-0 text-dark">{{ group.name }}</h5>
                </div>
                <span class="text-muted small fw-bold">{{ group.tests.length }} Tests Total</span>
              </div>

              <div v-show="!collapsedGroups[group.id]" class="card-body p-0 border-top">
                <div class="table-responsive">
                  <table class="table table-hover align-middle mb-0 text-sm">
                    <thead class="table-light">
                      <tr>
                        <th style="width: 100px;" class="text-center">ID</th>
                        <th style="width: 120px;" class="text-center">Jira ID</th>
                        <th>Test Case Template</th>
                        <th style="width: 150px;">Spreadsheet Link</th>
                        <th style="width: 180px;">Origin Sprint</th>
                        <th style="width: 110px;" class="text-center">Default Assignee</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="test in group.tests" :key="test.id">
                        <td class="text-center fw-bold text-muted" style="font-size: 11.5px; font-family: var(--bs-font-monospace, monospace); letter-spacing: 0.3px;">{{ test.task_code || '#' + test.id }}</td>
                        <td class="text-center">
                          <a v-if="test.jira_id && activeProject?.jira_board_link" :href="getJiraIssueUrl(test.jira_id)" target="_blank" class="badge text-white px-2 py-1 rounded d-inline-flex align-items-center gap-1 shadow-3xs text-decoration-none" style="background-color: #0052cc; font-size: 11px; transition: transform 0.2s;" onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">
                            <i class="fa-brands fa-jira text-white" style="font-size: 10px;"></i> {{ test.jira_id }}
                          </a>
                          <span v-else-if="test.jira_id" class="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-10 px-2 py-1 rounded d-inline-flex align-items-center gap-1" style="font-size: 11px;" title="Configure JIRA Board Link in Project settings to make this key clickable">
                            <i class="fa-brands fa-jira text-muted" style="font-size: 10px;"></i> {{ test.jira_id }}
                          </span>
                          <span v-else class="text-muted small">-</span>
                        </td>
                        <td>
                          <div class="d-flex align-items-center gap-2">
                            <span style="font-size: 13.5px; font-weight: 500;">{{ test.test }}</span>
                            <!-- Edit Action -->
                            <button class="btn btn-sm btn-link text-info p-0 border-0 edit-row-btn ms-2" data-bs-toggle="modal" data-bs-target="#editTestModal" @click="openEditTestModal(test)" title="Edit Test Details">
                              <i class="fa-solid fa-pen-to-square" style="font-size: 11.5px;"></i>
                            </button>
                          </div>
                        </td>
                        <td>
                          <a v-if="test.link" :href="test.link" target="_blank" class="btn btn-link btn-sm text-info p-0 text-decoration-none d-flex align-items-center gap-1">
                            <i class="fa-solid fa-file-arrow-up"></i>
                            <span class="small">{{ test.sheetId && test.sheetId !== '0' ? test.sheetId : 'Sheet' }}</span>
                          </a>
                          <span class="text-muted small" v-else>No Link</span>
                        </td>
                        <td>
                          <span v-if="test.originating_sprint_id" @click="jumpToSprint(test.originating_sprint_id)" class="badge origin-sprint-badge px-2.5 py-1.5 cursor-pointer hover-scale transition-all d-inline-flex align-items-center gap-1 shadow-2xs" style="font-size: 10.5px; font-weight: 600; border-radius: 6px;" title="Click to view this Sprint Run">
                            <i class="fa-solid fa-arrow-up-right-from-square" style="font-size: 8.5px;"></i> Origin: {{ test.originating_sprint_name || 'Sprint' }}
                          </span>
                          <span v-else class="badge bg-light text-muted border border-light-subtle px-2.5 py-1.5" style="font-size: 10.5px; font-weight: 550; border-radius: 6px;">
                            <i class="fa-solid fa-cubes-stacked me-1"></i> Library Origin
                          </span>
                        </td>
                        <td class="text-center">
                          <div class="dropdown d-inline-block">
                            <button class="btn btn-sm btn-white border d-flex align-items-center justify-content-between p-1" type="button" :id="'testerDropLib_' + test.id" data-bs-toggle="dropdown" aria-expanded="false" data-bs-boundary="viewport" style="width: 58px; border-radius: 8px; height: 32px; background: #fff;" :title="test.tester || 'Unassigned'">
                              <div class="d-flex align-items-center justify-content-center gap-1 overflow-hidden w-100">
                                <template v-if="!test.tester || test.tester === 'Unassigned'">
                                  <div class="avatar-circle-sm" style="background-color: #e5e7eb; color: #6b7280; font-size: 10px; font-weight: 700; width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                                    <i class="fa-solid fa-user" style="font-size: 9px;"></i>
                                  </div>
                                </template>
                                <template v-else>
                                  <div class="d-flex align-items-center flex-shrink-0">
                                    <div v-for="(name, idx) in test.tester.split(',').map(s => s.trim()).filter(Boolean)" :key="name" 
                                         class="avatar-circle-sm border border-white text-white d-flex align-items-center justify-content-center fw-bold position-relative shadow-3xs" 
                                         :style="{ 
                                           backgroundColor: getAvatarColor(name), 
                                           zIndex: 10 - idx, 
                                           marginLeft: idx > 0 ? '-6px' : '0px',
                                           width: '22px', 
                                           height: '22px', 
                                           borderRadius: '50%',
                                           fontSize: '9px',
                                           flexShrink: 0
                                         }"
                                         :title="name">
                                      {{ getInitials(name) }}
                                    </div>
                                  </div>
                                </template>
                                <i class="fa-solid fa-chevron-down text-muted" style="font-size: 8px; flex-shrink: 0;"></i>
                              </div>
                            </button>
                            <ul class="dropdown-menu shadow border-0 p-2" :aria-labelledby="'testerDropLib_' + test.id" style="max-height: 250px; overflow-y: auto; font-size: 13px; min-width: 200px; border-radius: 8px; background-color: #ffffff !important; z-index: 1060 !important;">
                              <li>
                                <div class="form-check py-1">
                                  <input class="form-check-input ms-checkbox-sm tester-check-input" type="checkbox" :checked="isTesterSelected(test, 'Unassigned')" @change="toggleTester(test, 'Unassigned')" :id="'testLib_' + test.id + '_unassigned'">
                                  <label class="form-check-label fw-bold text-dark ms-1 cursor-pointer" :for="'testLib_' + test.id + '_unassigned'">Unassigned</label>
                                </div>
                              </li>
                              <li><hr class="dropdown-divider"></li>
                              <li v-for="tOpt in listTestersFiltered" :key="tOpt">
                                <div class="form-check py-1">
                                  <input class="form-check-input ms-checkbox-sm tester-check-input" type="checkbox" :checked="isTesterSelected(test, tOpt)" @change="toggleTester(test, tOpt)" :id="'testLib_' + test.id + '_' + tOpt">
                                  <label class="form-check-label text-dark ms-1 cursor-pointer" :for="'testLib_' + test.id + '_' + tOpt">{{ tOpt }}</label>
                                </div>
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── TAB 3: AGILE TIMELINE AUDIT TRAIL ── -->
        <div v-else-if="activeTab === 'audit'">
          <div class="card shadow-sm border-0 mb-4 bg-white">
            <div class="card-body p-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div>
                <h5 class="fw-bold text-dark mb-1">
                  <i class="fa-solid fa-clock-rotate-left text-info me-2"></i> Regression Lifecycle Audit Trail
                </h5>
                <p class="text-muted small mb-0">Trace tests from creation, through active status executions, and across sprint-to-sprint migrations.</p>
              </div>

              <!-- Top Pagination Controls -->
              <div v-if="!isLoadingAudit && auditTrail.length > 0" class="d-flex align-items-center flex-wrap gap-3">
                <!-- Sort By -->
                <div class="d-flex align-items-center">
                  <span class="p-2 filterTitle small">Sort by:</span>
                  <select v-model="auditSortBy" class="form-select form-select-sm shadow-none filterOptions bordered-select" style="width: auto;">
                    <option v-for="sortOpt in auditSortByList" :value="sortOpt.value" :key="sortOpt.value">
                      {{ sortOpt.text }}
                    </option>
                  </select>
                </div>

                <!-- Rows per page -->
                <div class="d-flex align-items-center">
                  <span class="p-2 filterTitle small">Rows per page:</span>
                  <select v-model="auditPagination.pageSize" class="form-select form-select-sm shadow-none filterOptions bordered-select" style="width: auto;">
                    <option v-for="rowOpt in auditRowsPerPageList" :value="rowOpt.value" :key="rowOpt.value">
                      {{ rowOpt.text }}
                    </option>
                  </select>
                </div>

                <!-- Pagination Box -->
                <div class="pagination">
                  <button @click="previousAuditPage()" :disabled="auditPagination.page === 1" class="pagination-arrow">
                    <i class="fa fa-chevron-left"></i>
                  </button>
                  <span class="pagination-label">
                    <strong>{{ auditPaginationLabel }}</strong> of {{ auditPagination.totalItems }}
                  </span>
                  <button @click="nextAuditPage()" :disabled="auditPagination.page >= auditPagination.totalPages" class="pagination-arrow">
                    <i class="fa fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="isLoadingAudit" class="text-center py-5">
            <div class="spinner-border text-info" role="status" style="width: 2rem; height: 2rem;"></div>
            <p class="text-muted mt-2 small">Fetching historical timeline logs...</p>
          </div>

          <div v-else-if="auditTrail.length === 0" class="card shadow-sm border-0 bg-white p-5 text-center text-muted">
            <i class="fa-solid fa-clock-rotate-left fa-3x mb-3 text-light"></i>
            <h5 class="fw-bold">Audit Log is Empty</h5>
            <p class="small mb-0">Lifecycle events, such as creating, updating, or moving tasks between sprints, will log here automatically.</p>
          </div>

          <!-- Audit Timeline -->
          <div v-else class="timeline-container px-3 pb-5">
            <div class="timeline">
              <div v-for="log in auditTrail" :key="log.id" class="timeline-item d-flex gap-3 mb-4">
                <!-- Icon Bullet (Plain Vertex Blue with White Background) -->
                <div class="timeline-icon-bullet flex-shrink-0">
                  <i class="fa-solid" :class="getAuditActionIcon(log.action)"></i>
                </div>
                <!-- Log Content -->
                <div class="timeline-body card border-0 shadow-3xs bg-white p-3 flex-grow-1 animate-hover" style="border-radius: 12px; border-left: 4px solid #00A0C1 !important;">
                  <div class="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                    <div>
                      <span class="badge text-uppercase" :class="getAuditActionBadgeClass(log.action)" style="font-size: 10px; font-weight: 700; letter-spacing: 0.5px;">{{ log.action ? log.action.replace(/_/g, ' ') : '' }}</span>
                      <h6 class="fw-bold text-dark mb-0 mt-1" style="font-size: 14px;">{{ log.test_name }}</h6>
                    </div>
                    <small class="text-muted fw-bold" style="font-size: 11px;">{{ formatTimeAgo(log.created_at) }}</small>
                  </div>
                  <p class="text-muted small mb-1" style="line-height: 1.5;">{{ formatLogNotes(log.notes) }}</p>
                  <div class="d-flex align-items-center gap-2 mt-2 pt-2 border-top border-light border-opacity-50 flex-wrap">
                    <span class="badge bg-light text-muted border border-light-subtle rounded-pill" style="font-size: 10px;" v-if="log.to_sprint_name">
                      <i class="fa-solid fa-person-running text-info me-1"></i> Target: {{ log.to_sprint_name }}
                    </span>
                    <span class="badge bg-light text-muted border border-light-subtle rounded-pill" style="font-size: 10px;" v-if="log.from_sprint_name">
                      <i class="fa-solid fa-share me-1"></i> From: {{ log.from_sprint_name }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Bottom Pagination Controls -->
            <div class="d-flex justify-content-center align-items-center mt-4 pt-3 border-top border-light border-opacity-50">
              <span class="text-muted small">
                Showing <strong>{{ auditPaginationLabel }}</strong> of {{ auditPagination.totalItems }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── SHARED DIALOG MODALS ── -->

    <!-- Create Project Modal -->
    <div class="modal fade" id="createProjectModal" tabindex="-1" aria-labelledby="createProjectModalLabel" aria-hidden="true">
      <div class="modal-dialog" style="max-width: 600px; margin: 1.75rem auto;">
        <div class="modal-content border-0 shadow" style="max-height: 85vh; display: flex; flex-direction: column;">
          <div class="modal-header bg-light">
            <h5 class="modal-title fw-bold text-dark" id="createProjectModalLabel">
              <i class="fa-solid fa-folder-plus text-info me-2"></i> Create Regression Project
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4" style="overflow-y: auto; flex: 1 1 auto;">
            <div class="mb-3">
              <label for="projName" class="form-label fw-semibold text-muted small">Project Name <span class="text-danger">*</span></label>
              <input v-model="newProject.name" type="text" id="projName" class="form-control" placeholder="e.g. Spark Regression Library" required>
            </div>
            <div class="mb-3">
              <label for="projDesc" class="form-label fw-semibold text-muted small">Description</label>
              <textarea v-model="newProject.description" id="projDesc" class="form-control" rows="3" placeholder="Describe the scope of this regression test list..."></textarea>
            </div>
            <div class="mb-3">
              <label for="projPrefix" class="form-label fw-semibold text-muted small">Sprint Tag Abbreviation (Prefix)</label>
              <input v-model="newProject.prefix" type="text" id="projPrefix" class="form-control" placeholder="e.g. SP (for Spark), LD (for Leads)">
              <div class="form-text text-muted" style="font-size: 11px;">
                Used to format sprint badges dynamically (e.g. <code>SP26.05</code> from sprint number <code>05</code>).
              </div>
            </div>
            <div class="mb-3">
              <label for="projJira" class="form-label fw-semibold text-muted small">Jira Base Board URL / Link</label>
              <input v-model="newProject.jira_board_link" type="text" id="projJira" class="form-control" placeholder="e.g. https://company.atlassian.net">
              <div class="form-text text-muted" style="font-size: 11px;">
                Link to your project Jira workspace. Enabling this will automatically hyperlink tasks containing Jira IDs.
              </div>
            </div>
            
            <!-- Upload Custom Icon -->
            <div class="mb-3">
              <label class="form-label fw-semibold text-muted small d-flex justify-content-between align-items-center">
                <span>Custom Brand Logo / Icon File <span class="badge bg-light text-dark border ms-1">GCP Cloud</span></span>
                <span class="text-info small" style="font-size: 11px;">Optional</span>
              </label>
              <div class="p-3 border rounded bg-light bg-opacity-50">
                <input type="file" accept="image/*" class="form-control form-control-sm bg-white border" @change="onIconFileChange($event, 'create')" />
                <div v-if="newProject.encoded_icon" class="d-flex align-items-center justify-content-between mt-2 p-2 border rounded bg-white">
                  <div class="d-flex align-items-center gap-2">
                    <img :src="newProject.preview_icon" style="width: 28px; height: 28px; object-fit: contain; border-radius: 4px;" alt="Preview" />
                    <span class="small text-muted text-truncate" style="max-width: 150px;">{{ newProject.icon_filename }}</span>
                  </div>
                  <button type="button" class="btn btn-sm btn-link text-danger p-0 border-0 text-decoration-none fw-bold small" @click="newProject.encoded_icon = ''; newProject.icon_filename = ''; newProject.preview_icon = ''">Clear</button>
                </div>
                <div class="form-text text-muted mt-1" style="font-size: 11px;">
                  Upload custom PNG, JPG, or SVG. If uploaded, this image will take precedence over the selected FontAwesome icon.
                </div>
              </div>
            </div>
            
            <!-- Choose Project Icon -->
            <div class="mb-3">
              <label class="form-label fw-semibold text-muted small">Choose Project Icon</label>
              <div class="d-flex flex-wrap gap-2 p-2 border rounded bg-light bg-opacity-50">
                <button v-for="ico in availableIcons" :key="ico" type="button" class="btn btn-sm" :class="(newProject.icon === ico || (!newProject.icon && ico === 'fa-folder')) ? 'btn-info text-white' : 'btn-white border bg-white'" @click="newProject.icon = ico" style="width: 38px; height: 38px;" :title="ico">
                  <i class="fa-solid" :class="ico"></i>
                </button>
              </div>
            </div>

            <!-- Choose Theme Color -->
            <div class="mb-3">
              <label class="form-label fw-semibold text-muted small">Choose Theme Color</label>
              <div class="d-flex flex-wrap gap-3 p-3 border rounded bg-light bg-opacity-50">
                <button v-for="col in availableColors" :key="col" type="button" class="btn rounded-circle p-0 position-relative" :style="{ backgroundColor: col, width: '28px', height: '28px', border: (newProject.color === col || (!newProject.color && col === '#f59e0b')) ? '2px solid #000' : '1px solid #ced4da', transform: (newProject.color === col || (!newProject.color && col === '#f59e0b')) ? 'scale(1.15)' : 'none', transition: 'all 0.15s ease' }" @click="newProject.color = col" :title="col">
                  <span v-if="newProject.color === col || (!newProject.color && col === '#f59e0b')" class="position-absolute top-50 start-50 translate-middle text-white small" style="font-size: 10px;"><i class="fa-solid fa-check"></i></span>
                </button>
              </div>
            </div>
          </div>
          <div class="modal-footer bg-light border-top-0">
            <button type="button" class="btn btn-secondary btn-sm fw-bold" data-bs-dismiss="modal">Cancel</button>
            <button @click="saveNewProject" type="button" class="btn btn-info text-white btn-sm fw-bold">Create Project</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Project Modal -->
    <div class="modal fade" id="editProjectModal" tabindex="-1" aria-labelledby="editProjectModalLabel" aria-hidden="true">
      <div class="modal-dialog" style="max-width: 600px; margin: 1.75rem auto;">
        <div class="modal-content border-0 shadow" style="max-height: 85vh; display: flex; flex-direction: column;">
          <div class="modal-header bg-light">
            <h5 class="modal-title fw-bold text-dark" id="editProjectModalLabel">
              <i class="fa-solid fa-gear text-info me-2"></i> Edit Project Settings
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4" style="overflow-y: auto; flex: 1 1 auto;">
            <div class="mb-3">
              <label for="editProjName" class="form-label fw-semibold text-muted small">Project Name <span class="text-danger">*</span></label>
              <input v-model="editProject.name" type="text" id="editProjName" class="form-control" placeholder="e.g. Spark Regression Library" required>
            </div>
            <div class="mb-3">
              <label for="editProjDesc" class="form-label fw-semibold text-muted small">Description</label>
              <textarea v-model="editProject.description" id="editProjDesc" class="form-control" rows="3" placeholder="Describe the scope of this regression test list..."></textarea>
            </div>
            <div class="mb-3">
              <label for="editProjPrefix" class="form-label fw-semibold text-muted small">Sprint Tag Abbreviation (Prefix)</label>
              <input v-model="editProject.prefix" type="text" id="editProjPrefix" class="form-control" placeholder="e.g. SP (for Spark)">
              <div class="form-text text-muted" style="font-size: 11px;">
                Used to format sprint badges dynamically (e.g. <code>SP26.05</code> from sprint number <code>05</code>).
              </div>
            </div>
            <div class="mb-3">
              <label for="editProjJira" class="form-label fw-semibold text-muted small">Jira Base Board URL / Link</label>
              <input v-model="editProject.jira_board_link" type="text" id="editProjJira" class="form-control" placeholder="e.g. https://company.atlassian.net">
              <div class="form-text text-muted" style="font-size: 11px;">
                Link to your project Jira workspace. Enabling this will automatically hyperlink tasks containing Jira IDs.
              </div>
            </div>

            <!-- Upload Custom Icon -->
            <div class="mb-3">
              <label class="form-label fw-semibold text-muted small d-flex justify-content-between align-items-center">
                <span>Custom Brand Logo / Icon File <span class="badge bg-light text-dark border ms-1">GCP Cloud</span></span>
                <span class="text-info small" style="font-size: 11px;">Optional</span>
              </label>
              <div class="p-3 border rounded bg-light bg-opacity-50">
                <input type="file" accept="image/*" class="form-control form-control-sm bg-white border" @change="onIconFileChange($event, 'edit')" />
                <!-- Preview New Selection -->
                <div v-if="editProject.encoded_icon" class="d-flex align-items-center justify-content-between mt-2 p-2 border rounded bg-white">
                  <div class="d-flex align-items-center gap-2">
                    <img :src="editProject.preview_icon" style="width: 28px; height: 28px; object-fit: contain; border-radius: 4px;" alt="New Preview" />
                    <span class="small text-muted text-truncate" style="max-width: 150px;">{{ editProject.icon_filename }}</span>
                  </div>
                  <button type="button" class="btn btn-sm btn-link text-danger p-0 border-0 text-decoration-none fw-bold small" @click="editProject.encoded_icon = ''; editProject.icon_filename = ''; editProject.preview_icon = ''">Clear</button>
                </div>
                <!-- Show Current Logo if already a URL -->
                <div v-else-if="isUrl(editProject.icon)" class="d-flex align-items-center justify-content-between mt-2 p-2 border rounded bg-white">
                  <div class="d-flex align-items-center gap-2">
                    <img :src="editProject.icon" style="width: 28px; height: 28px; object-fit: contain; border-radius: 4px;" alt="Current Icon" />
                    <span class="small text-muted">Current Brand Logo</span>
                  </div>
                  <button type="button" class="btn btn-sm btn-link text-danger p-0 border-0 text-decoration-none fw-bold small" @click="editProject.icon = 'fa-folder'">Reset to Default Icon</button>
                </div>
                <div class="form-text text-muted mt-1" style="font-size: 11px;">
                  Upload custom PNG, JPG, or SVG. If uploaded, this image will take precedence over the selected FontAwesome icon.
                </div>
              </div>
            </div>

            <!-- Choose Project Icon -->
            <div class="mb-3">
              <label class="form-label fw-semibold text-muted small">Choose Project Icon</label>
              <div class="d-flex flex-wrap gap-2 p-2 border rounded bg-light bg-opacity-50">
                <button v-for="ico in availableIcons" :key="ico" type="button" class="btn btn-sm" :class="editProject.icon === ico ? 'btn-info text-white' : 'btn-white border bg-white'" @click="editProject.icon = ico" style="width: 38px; height: 38px;" :title="ico">
                  <i class="fa-solid" :class="ico"></i>
                </button>
              </div>
            </div>

            <!-- Choose Theme Color -->
            <div class="mb-3">
              <label class="form-label fw-semibold text-muted small">Choose Theme Color</label>
              <div class="d-flex flex-wrap gap-3 p-3 border rounded bg-light bg-opacity-50">
                <button v-for="col in availableColors" :key="col" type="button" class="btn rounded-circle p-0 position-relative" :style="{ backgroundColor: col, width: '28px', height: '28px', border: editProject.color === col ? '2px solid #000' : '1px solid #ced4da', transform: editProject.color === col ? 'scale(1.15)' : 'none', transition: 'all 0.15s ease' }" @click="editProject.color = col" :title="col">
                  <span v-if="editProject.color === col" class="position-absolute top-50 start-50 translate-middle text-white small" style="font-size: 10px;"><i class="fa-solid fa-check"></i></span>
                </button>
              </div>
            </div>
          </div>
          <div class="modal-footer bg-light border-top-0">
            <button type="button" class="btn btn-secondary btn-sm fw-bold" data-bs-dismiss="modal">Cancel</button>
            <button @click="saveEditProject" type="button" class="btn btn-info text-white btn-sm fw-bold">Save Changes</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Test Case Modal -->
    <div class="modal fade" id="editTestModal" tabindex="-1" aria-labelledby="editTestModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-md">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-light">
            <h5 class="modal-title fw-bold text-dark" id="editTestModalLabel">
              <i class="fa-solid fa-pen-to-square text-info me-2"></i> Edit Regression Test Case
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4">
            <div class="mb-3">
              <label for="editTestDesc" class="form-label fw-semibold text-muted small">Test Description <span class="text-danger">*</span></label>
              <input v-model="editTest.test" type="text" id="editTestDesc" class="form-control" required>
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold text-muted small d-flex justify-content-between align-items-center">
                <span>Category / Group Name <span class="text-danger">*</span></span>
                <a href="#" @click.prevent="isEditingNewGroup = !isEditingNewGroup" class="text-info text-decoration-none small fw-bold" style="font-size: 11.5px;">
                  {{ isEditingNewGroup ? 'Select Existing Category' : 'Create New Category' }}
                </a>
              </label>

              <!-- Dropdown Select for Existing Groups -->
              <div v-if="!isEditingNewGroup && testGroups.length > 0">
                <select v-model="editTest.group_name" class="form-select" required>
                  <option value="" disabled>-- Select a Category --</option>
                  <option v-for="g in testGroups" :key="g.id" :value="g.name">{{ g.name }}</option>
                </select>
              </div>

              <!-- Input text for New Custom Group -->
              <div v-else>
                <input v-model="editTest.group_name" type="text" id="editTestGroup" class="form-control" placeholder="e.g. Account Management" required>
              </div>
            </div>
            <div class="mb-3">
              <label for="editSheetLink" class="form-label fw-semibold text-muted small">Spreadsheet Link (Base Link)</label>
              <input v-model="editTest.spreadsheet_link" type="url" id="editSheetLink" class="form-control">
            </div>
            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="editSheetId" class="form-label fw-semibold text-muted small">Google Sheet gid</label>
                <input v-model="editTest.sheet_id" type="text" id="editSheetId" class="form-control">
              </div>
              <div class="col-md-6 mb-3">
                <label for="editCellRange" class="form-label fw-semibold text-muted small">Cell Range</label>
                <input v-model="editTest.cell_range" type="text" id="editCellRange" class="form-control">
              </div>
            </div>
            <div class="mb-3">
              <label for="editTestJira" class="form-label fw-semibold text-muted small">Jira Issue Key / ID</label>
              <input v-model="editTest.jira_id" type="text" id="editTestJira" class="form-control" placeholder="e.g. SP-412">
            </div>
          </div>
          <div class="modal-footer bg-light border-top-0">
            <button type="button" class="btn btn-secondary btn-sm fw-bold" data-bs-dismiss="modal">Cancel</button>
            <button @click="saveEditTest" type="button" class="btn btn-info text-white btn-sm fw-bold">Save Changes</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Test Case Modal -->
    <div class="modal fade" id="addTestModal" tabindex="-1" aria-labelledby="addTestModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-md">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-light">
            <h5 class="modal-title fw-bold text-dark" id="addTestModalLabel">
              <i class="fa-solid fa-circle-plus text-info me-2"></i> Add Regression Test Case
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4">
            <div class="mb-3">
              <label for="testDesc" class="form-label fw-semibold text-muted small">Test Description <span class="text-danger">*</span></label>
              <input v-model="newTest.test" type="text" id="testDesc" class="form-control" placeholder="e.g. Admin should be able to view application logs" required>
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold text-muted small d-flex justify-content-between align-items-center">
                <span>Category / Group Name <span class="text-danger">*</span></span>
                <a href="#" @click.prevent="isCreatingNewGroup = !isCreatingNewGroup" class="text-info text-decoration-none small fw-bold" style="font-size: 11.5px;">
                  {{ isCreatingNewGroup ? 'Select Existing Category' : 'Create New Category' }}
                </a>
              </label>

              <!-- Dropdown Select for Existing Groups -->
              <div v-if="!isCreatingNewGroup && testGroups.length > 0">
                <select v-model="newTest.group_name" class="form-select" required>
                  <option value="" disabled>-- Select a Category --</option>
                  <option v-for="g in testGroups" :key="g.id" :value="g.name">{{ g.name }}</option>
                </select>
              </div>

              <!-- Input text for New Custom Group -->
              <div v-else>
                <input v-model="newTest.group_name" type="text" id="testGroup" class="form-control" placeholder="e.g. Account Management" required>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="testStatus" class="form-label fw-semibold text-muted small">Initial Status</label>
                <select v-model="newTest.status" id="testStatus" class="form-select">
                  <option value="not_started">Not Started</option>
                  <option value="in_progress">In Progress</option>
                  <option value="passed">Passed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label fw-semibold text-muted small">Initial Assignee(s)</label>
                <div class="dropdown">
                  <button class="btn btn-sm btn-light border dropdown-toggle w-100 text-truncate text-start" type="button" id="newTestTesterDropdown" data-bs-toggle="dropdown" aria-expanded="false" style="background: white;">
                    <span>{{ newTestTesters.join(', ') }}</span>
                  </button>
                  <ul class="dropdown-menu shadow border-0 p-2" aria-labelledby="newTestTesterDropdown" style="max-height: 200px; overflow-y: auto; font-size: 13px; min-width: 220px;">
                    <li>
                      <div class="form-check py-1">
                        <input class="form-check-input" type="checkbox" value="Unassigned" :checked="newTestTesters.includes('Unassigned')" @change="toggleNewTestTester('Unassigned')" id="new_unassigned">
                        <label class="form-check-label fw-bold text-dark ms-1" for="new_unassigned">Unassigned</label>
                      </div>
                    </li>
                    <li><hr class="dropdown-divider"></li>
                    <li v-for="tester in listTestersFiltered" :key="tester">
                      <div class="form-check py-1">
                        <input class="form-check-input" type="checkbox" :value="tester" :checked="newTestTesters.includes(tester)" @change="toggleNewTestTester(tester)" :id="'new_' + tester">
                        <label class="form-check-label text-dark ms-1" :for="'new_' + tester">{{ tester }}</label>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="mb-3">
              <label for="sheetLink" class="form-label fw-semibold text-muted small">Spreadsheet Link (Base Link)</label>
              <input v-model="newTest.spreadsheet_link" type="url" id="sheetLink" class="form-control" placeholder="https://docs.google.com/spreadsheets/d/...">
            </div>
            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="sheetId" class="form-label fw-semibold text-muted small">Google Sheet gid</label>
                <input v-model="newTest.sheet_id" type="text" id="sheetId" class="form-control" placeholder="e.g. 0">
              </div>
              <div class="col-md-6 mb-3">
                <label for="cellRange" class="form-label fw-semibold text-muted small">Cell Range</label>
                <input v-model="newTest.cell_range" type="text" id="cellRange" class="form-control" placeholder="e.g. A12:B12">
              </div>
            </div>
            <div class="mb-3">
              <label for="newTestJira" class="form-label fw-semibold text-muted small">Jira Issue Key / ID</label>
              <input v-model="newTest.jira_id" type="text" id="newTestJira" class="form-control" placeholder="e.g. SP-412">
            </div>

            <!-- Option to Add to Master Test Library (only shown if activeTab is "sprints") -->
            <div v-if="activeTab === 'sprints'" class="mb-3 d-flex flex-column gap-1 bg-light p-3 rounded-3 border animate-fade-in">
              <div class="form-check form-switch d-flex align-items-center gap-2">
                <input v-model="newTest.add_to_library" class="form-check-input vertex-switch" type="checkbox" id="addToMasterLibrary" style="cursor: pointer; width: 40px; height: 20px;">
                <label class="form-check-label fw-bold text-dark ms-1 cursor-pointer" for="addToMasterLibrary">
                  Add to Master Test Library
                </label>
              </div>
              <div class="text-muted small ps-5 mt-1" style="font-size: 11.5px; line-height: 1.4;">
                <span v-if="newTest.add_to_library" class="text-success fw-semibold">
                  <i class="fa-solid fa-cloud-arrow-up me-1"></i> Added to both the Master Test Library and this active sprint run.
                </span>
                <span v-else class="text-warning fw-semibold">
                  <i class="fa-solid fa-ghost me-1"></i> Shadow Copy: Created ONLY inside this active sprint run (isolated sprint perspective).
                </span>
              </div>
            </div>
          </div>
          <div class="modal-footer bg-light border-top-0">
            <button type="button" class="btn btn-secondary btn-sm fw-bold" data-bs-dismiss="modal">Cancel</button>
            <button @click="saveNewTest" type="button" class="btn btn-info text-white btn-sm fw-bold">Add Test Case</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Define Sprint Modal -->
    <div class="modal fade" id="createSprintModal" tabindex="-1" aria-labelledby="createSprintModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-md">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-light">
            <h5 class="modal-title fw-bold text-dark" id="createSprintModalLabel">
              <i class="fa-solid fa-person-running text-info me-2"></i> Define Sprint Run
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4">
            <div class="mb-3">
              <label for="sprintName" class="form-label fw-semibold text-muted small d-flex justify-content-between align-items-center w-100 mb-2">
                <span>Sprint Name <span class="text-danger">*</span></span>
                <span v-if="sprints && sprints.length > 0" class="text-secondary small fw-normal" style="font-size: 11px;">
                  Last sprint: <strong class="text-dark bg-secondary bg-opacity-10 px-2 py-0.5 rounded">{{ sprints[0].name }}</strong>
                </span>
              </label>
              <input v-model="newSprint.name" type="text" id="sprintName" class="form-control" placeholder="e.g. SP26.01" required>
            </div>
            <div class="mb-3 d-flex flex-column gap-1 bg-light p-3 rounded-3 border">
              <div class="form-check form-switch d-flex align-items-center gap-2">
                <input v-model="newSprint.start_immediately" class="form-check-input vertex-switch" type="checkbox" id="sprintStartImmediately" style="cursor: pointer; width: 40px; height: 20px;">
                <label class="form-check-label fw-bold text-dark ms-1 cursor-pointer" for="sprintStartImmediately">
                  Activate & start execution immediately
                </label>
              </div>
              <div class="text-muted small ps-5 mt-1" style="font-size: 11.5px; line-height: 1.4;">
                <span v-if="newSprint.start_immediately" class="text-success fw-bold">
                  <i class="fa-solid fa-bolt me-1"></i> Sprint starts immediately as the active run.
                </span>
                <span v-else class="text-info fw-bold">
                  <i class="fa-solid fa-hourglass-half me-1"></i> Sprint initializes as a background "Planned" draft. You can carry over tasks in the background without affecting your active run.
                </span>
              </div>
            </div>

            <!-- Warning: Active Sprint Already Exists -->
            <div v-if="activeSprint && activeSprint.status === 'active' && newSprint.start_immediately" class="alert alert-warning border border-warning border-opacity-50 p-3 rounded-3 mb-3 d-flex align-items-start gap-2 animate-fade-in" style="background-color: #fffbeb;">
              <i class="fa-solid fa-triangle-exclamation text-warning fa-lg mt-1"></i>
              <div>
                <strong class="text-dark small d-block mb-1">Active Sprint Already Running</strong>
                <span class="text-muted small" style="font-size: 11.5px; line-height: 1.4; display: block;">
                  Sprint <strong class="text-dark">"{{ activeSprint.name }}"</strong> is currently active on this project. 
                  Proceeding with instant activation will <strong>automatically close and archive</strong> "{{ activeSprint.name }}" as completed.
                </span>
              </div>
            </div>

            <div class="mb-3">
              <label for="populateSource" class="form-label fw-semibold text-muted small">Populate Tests From</label>
              <select v-model="newSprint.populate_source" id="populateSource" class="form-select bg-white">
                <option value="library">Master Test Case Library (Recommended)</option>
                <option value="previous" :disabled="sprints.length === 0">Carry Over All Tests From Previous Sprint</option>
                <option value="failed_only" :disabled="sprints.length === 0">Carry Over ONLY Failed/Incomplete Tests From Previous Sprint</option>
                <option value="custom">Custom Selection (Cherry-pick from Master/Previous)</option>
                <option value="none">None (Start with an Empty Sprint Run)</option>
              </select>
            </div>
            <div class="mb-3" v-if="newSprint.populate_source === 'previous' || newSprint.populate_source === 'failed_only'">
              <label for="prevSprintId" class="form-label fw-semibold text-muted small">Select Source Sprint <span class="text-danger">*</span></label>
              <select v-model="newSprint.prev_sprint_id" id="prevSprintId" class="form-select bg-white">
                <option :value="null" disabled>-- Choose Sprint --</option>
                <option v-for="s in sprints" :key="s.id" :value="s.id">{{ s.name }} ({{ s.status }})</option>
              </select>
            </div>
            <div class="mb-3" v-if="newSprint.populate_source === 'custom'">
              <div class="row g-2 mb-3">
                <div class="col-6">
                  <label class="form-label fw-semibold text-muted small">Select Custom Source</label>
                  <select v-model="newSprint.custom_source" @change="onCustomSourceChange" class="form-select bg-white">
                    <option value="library">Master Test Case Library</option>
                    <option value="previous">Previous Sprint Run</option>
                  </select>
                </div>
                <div class="col-6" v-if="newSprint.custom_source === 'previous'">
                  <label class="form-label fw-semibold text-muted small">Select Source Sprint <span class="text-danger">*</span></label>
                  <select v-model="newSprint.prev_sprint_id" @change="onCustomPrevSprintChange" class="form-select bg-white">
                    <option :value="null" disabled>-- Choose Sprint --</option>
                    <option v-for="s in sprints" :key="s.id" :value="s.id">{{ s.name }} ({{ s.status }})</option>
                  </select>
                </div>
              </div>

              <!-- Scrollable Selection List -->
              <div v-if="newSprint.custom_source === 'library' || (newSprint.custom_source === 'previous' && newSprint.prev_sprint_id)" class="border rounded p-3 bg-light bg-opacity-50" style="max-height: 250px; overflow-y: auto;">
                <div v-if="isLoadingCustomSourceTasks" class="text-center py-4">
                  <div class="spinner-border spinner-border-sm text-info" role="status"></div>
                  <span class="ms-2 small text-muted">Loading tasks...</span>
                </div>
                <div v-else-if="filteredCustomSourceTasks.length === 0" class="text-center py-4 text-muted small">
                  No tasks found in this source.
                </div>
                <div v-else>
                  <div class="d-flex align-items-center justify-content-between pb-2 mb-2 border-bottom">
                    <div class="form-check">
                      <input class="form-check-input custom-teal-checkbox" type="checkbox" :checked="isAllCustomSelected" @change="toggleCustomSelectAll" id="customSelectAll" style="cursor: pointer;">
                      <label class="form-check-label fw-bold text-dark ms-1 cursor-pointer" for="customSelectAll" style="font-size: 13px;">
                        Select All ({{ filteredCustomSourceTasks.length }} tasks)
                      </label>
                    </div>
                    <span class="badge bg-secondary bg-opacity-10 text-secondary" style="font-size: 11px;">
                      {{ selectedCustomCount }} / {{ filteredCustomSourceTasks.length }} selected
                    </span>
                  </div>
                  
                  <div v-for="t in filteredCustomSourceTasks" :key="t.id" class="d-flex align-items-center justify-content-between py-1 border-bottom border-light">
                    <div class="form-check text-truncate pe-3">
                      <input class="form-check-input custom-teal-checkbox" type="checkbox" v-model="newSprint.custom_test_ids" :value="t.id" style="cursor: pointer;">
                      <div class="form-check-label text-dark text-truncate ms-1" style="font-size: 13px; max-width: 500px;">
                        <strong class="text-secondary small">{{ t.task_code || t.test_id || 'Shadow' }}:</strong> {{ t.test_description || t.test }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer bg-light border-top-0">
            <button type="button" class="btn btn-secondary btn-sm fw-bold" data-bs-dismiss="modal">Cancel</button>
            <button @click="saveNewSprint" type="button" class="btn btn-info text-white btn-sm fw-bold">Initialize Sprint</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Port / Copy Tasks Modal -->
    <div class="modal fade" id="portTasksModal" tabindex="-1" aria-labelledby="portTasksModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content border-0 shadow-lg" style="border-radius: 12px; overflow: hidden;">
          <div class="modal-header border-0 p-3 d-flex align-items-center justify-content-between" style="background: #f8f9fa !important; border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important;">
            <h5 class="modal-title fw-bold text-dark mb-0 d-flex align-items-center gap-2" id="portTasksModalLabel">
              <i class="fa-solid fa-file-import text-info fs-5"></i> Port / Copy Tasks to {{ activeSprint?.name }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4">
            <p class="text-muted small mb-4">Select a previous sprint run to copy tasks from. You can filter by status or manually cherry-pick specific tasks to port over into your current sprint run.</p>
            
            <div class="row mb-3">
              <div class="col-md-6">
                <label for="portSourceSprint" class="form-label fw-semibold text-muted small">Select Source Sprint <span class="text-danger">*</span></label>
                <select v-model="portForm.sourceSprintId" id="portSourceSprint" @change="onPortSourceSprintChange" class="form-select bg-white">
                  <option :value="null" disabled>-- Choose Sprint --</option>
                  <option v-for="s in otherSprints" :key="s.id" :value="s.id">{{ s.name }} ({{ s.status }})</option>
                </select>
              </div>
              <div class="col-md-6">
                <label for="portFilterMode" class="form-label fw-semibold text-muted small">Task Filtering</label>
                <select v-model="portForm.filterMode" id="portFilterMode" @change="applyPortFilter" class="form-select bg-white">
                  <option value="all">Port ALL Tasks</option>
                  <option value="failed_incomplete">Port ONLY Failed & Incomplete Tasks</option>
                </select>
              </div>
            </div>

            <div class="form-check mb-4">
              <input class="form-check-input custom-teal-checkbox" type="checkbox" v-model="portForm.resetTester" id="portResetTester" style="cursor: pointer;">
              <label class="form-check-label fw-semibold text-dark ms-1 cursor-pointer" for="portResetTester" style="font-size: 13px;">
                Reset tester assignments to "Unassigned" on ported tasks
              </label>
            </div>

            <!-- Task Cherry-Picking Area -->
            <div v-if="portForm.sourceSprintId" class="border rounded p-3 bg-light bg-opacity-50" style="max-height: 280px; overflow-y: auto;">
              <div v-if="isLoadingSourceTasks" class="text-center py-4">
                <div class="spinner-border spinner-border-sm text-info" role="status"></div>
                <span class="ms-2 small text-muted">Loading source tasks...</span>
              </div>
              <div v-else-if="filteredSourceTasks.length === 0" class="text-center py-4 text-muted small">
                No matching tasks found in this source sprint.
              </div>
              <div v-else>
                <div class="d-flex align-items-center justify-content-between pb-2 mb-2 border-bottom">
                  <div class="form-check">
                    <input class="form-check-input custom-teal-checkbox" type="checkbox" :checked="isAllPortSelected" @change="togglePortSelectAll" id="portSelectAll" style="cursor: pointer;">
                    <label class="form-check-label fw-bold text-dark ms-1 cursor-pointer" for="portSelectAll" style="font-size: 13px;">
                      Select All ({{ filteredSourceTasks.length }} tasks)
                    </label>
                  </div>
                  <span class="badge bg-secondary bg-opacity-10 text-secondary" style="font-size: 11px;">
                    {{ selectedPortCount }} / {{ filteredSourceTasks.length }} selected
                  </span>
                </div>
                
                <div v-for="t in filteredSourceTasks" :key="t.id" class="d-flex align-items-center justify-content-between py-1 border-bottom border-light">
                  <div class="form-check text-truncate pe-3">
                    <input class="form-check-input custom-teal-checkbox" type="checkbox" v-model="portForm.selectedTaskIds" :value="t.id" style="cursor: pointer;">
                    <div class="form-check-label text-dark text-truncate ms-1" style="font-size: 13px; max-width: 500px;">
                      <strong class="text-secondary small">{{ t.task_code || t.test_id || 'Shadow' }}:</strong> {{ t.test_description || t.test }}
                    </div>
                  </div>
                  <div class="d-flex align-items-center gap-2">
                    <span class="badge" :class="{
                      'bg-success bg-opacity-10 text-success': t.status === 'passed',
                      'bg-danger bg-opacity-10 text-danger': t.status === 'failed',
                      'bg-warning bg-opacity-10 text-warning': t.status === 'blocked',
                      'bg-secondary bg-opacity-10 text-secondary': t.status === 'not_started' || t.status === 'not_run'
                    }" style="font-size: 10px; text-transform: uppercase;">
                      {{ t.status }}
                    </span>
                    <span class="badge bg-dark bg-opacity-10 text-dark" style="font-size: 10px;">{{ t.tester || 'Unassigned' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer bg-light border-top-0">
            <button type="button" class="btn btn-secondary btn-sm fw-bold" data-bs-dismiss="modal">Cancel</button>
            <button @click="executePortTasks" type="button" :disabled="!portForm.sourceSprintId || selectedPortCount === 0 || isPorting" class="btn btn-info text-white btn-sm fw-bold">
              <span v-if="isPorting" class="spinner-border spinner-border-sm me-1" role="status"></span>
              Port Selected Tasks
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Reusable Premium Confirmation Modal -->
    <div class="modal fade" id="confirmModal" tabindex="-1" aria-labelledby="confirmModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-md">
        <div class="modal-content border-0 shadow-lg" style="border-radius: 12px; overflow: hidden;">
          <div class="modal-header border-0 p-3 d-flex align-items-center justify-content-between" style="background: #f8f9fa !important; border-bottom: none !important;">
            <h5 class="modal-title fw-bold text-dark mb-0 d-flex align-items-center gap-2" id="confirmModalLabel">
              <i class="fa-solid" :class="confirmModal.icon"></i> {{ confirmModal.title }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4 text-center">
            <div class="bg-opacity-10 p-3 rounded-circle d-inline-flex mb-3" :class="confirmModal.icon.includes('text-danger') ? 'bg-danger text-danger' : (confirmModal.icon.includes('text-warning') ? 'bg-warning text-warning' : 'bg-info text-info')" style="background-color: rgba(0, 160, 193, 0.1);">
              <i class="fa-solid fa-triangle-exclamation fa-3x" :class="confirmModal.icon"></i>
            </div>
            <h5 class="fw-bold text-dark mb-2">{{ confirmModal.title }}</h5>
            <p class="text-muted small mb-0 px-2" style="font-size: 13.5px; line-height: 1.5; white-space: pre-line;">
              {{ confirmModal.message }}
            </p>
          </div>
          <div class="modal-footer border-0 bg-light p-3 d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-outline-secondary btn-sm fw-bold px-3" style="border-radius: 8px;" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-sm fw-bold px-3" :class="confirmModal.confirmBtnClass" style="border-radius: 8px;" @click="handleConfirmModalAction">{{ confirmModal.confirmBtnText }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom CSV Confirm Modal -->
    <div class="modal fade" id="csvConfirmModal" tabindex="-1" aria-labelledby="csvConfirmModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-md">
        <div class="modal-content border-0 shadow-lg" style="border-radius: 12px; overflow: hidden;">
          <div class="modal-header border-0 p-3 d-flex align-items-center justify-content-between" style="background: #f8f9fa !important; border-bottom: none !important;">
            <h5 class="modal-title fw-bold text-dark mb-0 d-flex align-items-center gap-2" id="csvConfirmModalLabel">
              <i class="fa-solid fa-file-import text-info fs-5"></i> Confirm CSV Import
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4 text-center">
            <div class="bg-info bg-opacity-10 p-3 rounded-circle d-inline-flex mb-3">
              <i class="fa-solid fa-file-csv text-info fa-3x"></i>
            </div>
            <h5 class="fw-bold text-dark mb-2">Import Test Library?</h5>
            <p class="text-muted small mb-0 px-2" style="font-size: 13.5px; line-height: 1.5;">
              Are you sure you want to import test cases from <strong class="text-info">"{{ csvFileName }}"</strong>?<br>
              The system will parse the records, perform custom column-mapping, and automatically skip any case-insensitive duplicates.
            </p>
          </div>
          <div class="modal-footer border-0 bg-light p-3 d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-outline-secondary btn-sm fw-bold px-3" style="border-radius: 8px;" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-info text-white btn-sm fw-bold px-3" style="border-radius: 8px;" @click="confirmCSVImport">Start Import</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom CSV Receipt Success Modal -->
    <div class="modal fade" id="csvReceiptModal" tabindex="-1" aria-labelledby="csvReceiptModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-md">
        <div class="modal-content border-0 shadow-lg" style="border-radius: 12px; overflow: hidden;">
          <div class="modal-header border-0 p-3 d-flex align-items-center justify-content-between" style="background: #e8f7f0 !important; border-bottom: none !important;">
            <h5 class="modal-title fw-bold text-success mb-0 d-flex align-items-center gap-2" id="csvReceiptModalLabel">
              <i class="fa-solid fa-circle-check fs-5"></i> Import Completed Successfully
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4">
            <div class="text-center mb-4">
              <div class="bg-success bg-opacity-10 p-3 rounded-circle d-inline-flex mb-3">
                <i class="fa-solid fa-cloud-arrow-up text-success fa-3x"></i>
              </div>
              <h5 class="fw-bold text-dark">CSV Import Receipt</h5>
              <p class="text-muted small">Your file has been parsed and integrated into the Master Test Library.</p>
            </div>
            
            <div class="bg-light rounded-3 p-3 border border-light-subtle">
              <div class="d-flex justify-content-between align-items-center py-2 border-bottom border-light">
                <span class="text-muted small fw-semibold">Total Rows Parsed:</span>
                <span class="badge bg-secondary px-2 py-1 fw-bold fs-6" style="border-radius: 6px;">{{ csvImportReceipt.total_rows }}</span>
              </div>
              <div class="d-flex justify-content-between align-items-center py-2 border-bottom border-light">
                <span class="text-muted small fw-semibold">Successfully Imported:</span>
                <span class="badge bg-success px-2 py-1 fw-bold fs-6" style="border-radius: 6px;">{{ csvImportReceipt.imported }}</span>
              </div>
              <div class="d-flex justify-content-between align-items-center py-2">
                <span class="text-muted small fw-semibold">Duplicates Skipped:</span>
                <span class="badge bg-warning text-dark px-2 py-1 fw-bold fs-6" style="border-radius: 6px;">{{ csvImportReceipt.skipped_duplicates }}</span>
              </div>
            </div>
          </div>
          <div class="modal-footer border-0 bg-light p-3">
            <button type="button" class="btn btn-success text-white btn-sm fw-bold w-100" style="border-radius: 8px; height: 38px;" data-bs-dismiss="modal">Awesome, Got It!</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Move/Carryover Task Modal -->
    <div class="modal fade" id="moveSprintTestModal" tabindex="-1" aria-labelledby="moveSprintTestModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-md">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-light">
            <h5 class="modal-title fw-bold text-dark" id="moveSprintTestModalLabel">
              <i class="fa-solid fa-share-from-square text-info me-2"></i> Move to Sprint Run
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4">
            <p class="text-muted small mb-4">Carry this test execution item over to another active or planned sprint run. Its status in the current sprint will be set to <strong>"Moved"</strong> for complete audit trace logs.</p>
            
            <div class="form-check mb-3">
              <input class="form-check-input custom-teal-checkbox" type="checkbox" v-model="moveTaskForm.createNew" id="moveCreateNew" style="cursor: pointer;">
              <label class="form-check-label fw-bold text-dark ms-1 cursor-pointer" for="moveCreateNew">
                Create a brand new sprint run on-the-fly for this task
              </label>
            </div>

            <div class="mb-3" v-if="moveTaskForm.createNew">
              <label for="moveNewSprintName" class="form-label fw-semibold text-muted small">New Sprint Name <span class="text-danger">*</span></label>
              <input v-model="moveTaskForm.newSprintName" type="text" id="moveNewSprintName" class="form-control" placeholder="e.g. Sprint 02" required>
            </div>

            <div class="mb-3" v-else>
              <label for="targetSprintId" class="form-label fw-semibold text-muted small">Select Target Sprint <span class="text-danger">*</span></label>
              <select v-model="moveTaskForm.targetSprintId" id="targetSprintId" class="form-select bg-white">
                <option :value="null" disabled>-- Choose Sprint --</option>
                <option v-for="s in sprints.filter(s => s.id !== activeSprintId)" :key="s.id" :value="s.id">{{ s.name }} ({{ s.status }})</option>
              </select>
            </div>
          </div>
          <div class="modal-footer bg-light border-top-0">
            <button type="button" class="btn btn-secondary btn-sm fw-bold" data-bs-dismiss="modal">Cancel</button>
            <button @click="executeMoveTask" type="button" class="btn btn-info text-white btn-sm fw-bold" :disabled="!moveTaskForm.createNew && !moveTaskForm.targetSprintId">Execute Move</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Report Modal / Summary Overlay -->
    <div class="modal fade" id="reportPreviewModal" tabindex="-1" aria-labelledby="reportPreviewModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title fw-bold" id="reportPreviewModalLabel">Regression Test Report Summary</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4" id="pdfReportContent">
            <div class="text-center mb-4 border-bottom pb-3">
              <h2 class="fw-bold mb-1" style="color: #435666;">{{ activeProject ? activeProject.name : 'Regression Library' }} Report</h2>
              <p class="text-muted small mb-0">Generated on {{ formattedCurrentDate }} | Spark Development Platform</p>
            </div>
            
            <div class="row mb-4">
              <div class="col-md-6">
                <h6 class="fw-bold text-muted small uppercase mb-2">Execution Metrics</h6>
                <table class="table table-sm border-0">
                  <tbody>
                    <tr><td class="text-muted">Total Test Cases:</td><td class="fw-bold">{{ totalTests }}</td></tr>
                    <tr><td class="text-muted">Passed:</td><td class="fw-bold text-success">{{ passedCount }} ({{ totalTests > 0 ? Math.round(passedCount / totalTests * 100) : 0 }}%)</td></tr>
                    <tr><td class="text-muted">In Progress:</td><td class="fw-bold text-warning">{{ inProgressCount }}</td></tr>
                    <tr><td class="text-muted">Failed:</td><td class="fw-bold text-danger">{{ failedCount }}</td></tr>
                    <tr><td class="text-muted">Not Started:</td><td class="fw-bold text-secondary">{{ notStartedCount }}</td></tr>
                  </tbody>
                </table>
              </div>
              <div class="col-md-6 border-start ps-4">
                <h6 class="fw-bold text-muted small uppercase mb-2">Category Highlights</h6>
                <div class="d-flex flex-column gap-2">
                  <div v-for="group in testGroups" :key="group.id" class="d-flex justify-content-between align-items-center small">
                    <span class="text-truncate" style="max-width: 180px;">{{ group.name }}:</span>
                    <span class="fw-bold">{{ getPassedCountForGroup(group) }}/{{ group.tests.length }} Passed</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-4 pt-3 border-top text-center text-muted small">
              <p>Spark Platform Regression testing dashboard and automated results logging. Vertex Cyber Security.</p>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary btn-sm" data-bs-dismiss="modal">Close</button>
            <button @click="printReport" class="btn btn-info text-white btn-sm fw-bold">
              <i class="fa-solid fa-print me-1"></i> Print/Save PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal as BootstrapModal } from 'bootstrap';

export default {
  name: "RegressionLibraryDashboard",
  data() {
    return {
      currentView: "dashboard", // "dashboard" or "project"
      projects: [],
      activeProjectId: 1,
      rawTests: [],
      testGroups: [],
      collapsedGroups: {},
      searchQuery: "",
      filterTester: "",
      filterStatus: "",
      filterChecked: "",
      filterSprint: "",
      viewMode: "table",
      filterAssignedToMe: false,
      listTesters: ["Unassigned"],
      availableTesters: [],
      availableBDMs: [],
      draggedTestId: null,
      draggedGroupId: null,
      isLoading: false,
      isCreatingNewGroup: false,
      isEditingNewGroup: false,

      // CSV Import Modal states
      showCSVConfirmModal: false,
      showCSVReceiptModal: false,
      csvFileName: "",
      csvSelectedFile: null,
      csvImportReceipt: {
        total_rows: 0,
        imported: 0,
        skipped_duplicates: 0,
      },

      // Reusable Premium Confirmation Modal states
      confirmModal: {
        title: "Confirm Action",
        message: "",
        icon: "fa-triangle-exclamation text-warning",
        confirmBtnText: "Confirm",
        confirmBtnClass: "btn-info text-white",
        onConfirm: null,
      },

      // Agile State Properties
      activeTab: "sprints", // "sprints", "library", "audit"
      sprints: [],
      activeSprintId: null,
      activeSprintTests: [],
      auditTrail: [],
      auditPagination: {
        page: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 1,
      },
      auditSortBy: "dateCreated_desc",
      auditSortByList: [
        { text: "Newest", value: "dateCreated_desc" },
        { text: "Oldest", value: "dateCreated_asc" }
      ],
      auditRowsPerPageList: [
        { text: "5", value: 5 },
        { text: "10", value: 10 },
        { text: "20", value: 20 },
        { text: "50", value: 50 }
      ],
      isLoadingSprints: false,
      isLoadingSprintTests: false,
      isLoadingAudit: false,
      newSprint: {
        name: "",
        populate_source: "library",
        prev_sprint_id: null,
        start_immediately: false,
        custom_source: "library",
        custom_test_ids: [],
      },
      customSourceTasks: [],
      isLoadingCustomSourceTasks: false,
      moveTaskForm: {
        sprintTestId: null,
        testId: null,
        targetSprintId: null,
        newSprintName: "",
        createNew: false,
      },

      // Port / Copy Tasks State
      portForm: {
        sourceSprintId: null,
        filterMode: "all",
        resetTester: false,
        selectedTaskIds: [],
      },
      isLoadingSourceTasks: false,
      isPorting: false,
      portSourceTasks: [],

      availableIcons: [
        "fa-folder",
        "fa-server",
        "fa-database",
        "fa-cloud",
        "fa-laptop-code",
        "fa-shield-halved",
        "fa-vial",
        "fa-chart-line"
      ],
      availableColors: [
        "#00A0C1", // Vertex Blue
        "#10B981", // Emerald Green
        "#F59E0B", // Amber Orange
        "#EF4444", // Rose Red
        "#8B5CF6", // Amethyst Violet
        "#3B82F6", // Indigo Blue
        "#EC4899"  // Fuchsia Pink
      ],

      // Modal helper forms
      newProject: {
        name: "",
        description: "",
        prefix: "",
        jira_board_link: "",
        icon: "fa-folder",
        color: "#f59e0b",
        encoded_icon: "",
        icon_filename: "",
        preview_icon: "",
      },
      editProject: {
        id: null,
        name: "",
        description: "",
        prefix: "",
        jira_board_link: "",
        icon: "fa-folder",
        color: "#f59e0b",
        encoded_icon: "",
        icon_filename: "",
        preview_icon: "",
      },
      newTest: {
        group_name: "",
        test: "",
        spreadsheet_link: "",
        sheet_id: "",
        cell_range: "",
        status: "not_started",
        tester: "Unassigned",
        sprint_number: "",
        jira_id: "",
      },
      editTest: {
        id: null,
        group_name: "",
        test: "",
        spreadsheet_link: "",
        sheet_id: "",
        cell_range: "",
        sprint_number: "",
        jira_id: "",
      },
      newTestTesters: ["Unassigned"],
      createProjectModal: null,
      addTestModal: null,
    };
  },

  created() {
    this.fetchProjects();
    this.fetchBDMs();
  },

  mounted() {
    this.renderDonutChart();
  },

  computed: {
    activeProject() {
      return this.projects.find(p => p.id === this.activeProjectId) || null;
    },
    auditPaginationLabel() {
      if (this.auditPagination.totalItems === 0) return "0 - 0";
      const start = Math.min((this.auditPagination.page - 1) * this.auditPagination.pageSize + 1, this.auditPagination.totalItems);
      const end = Math.min((this.auditPagination.page * this.auditPagination.pageSize), this.auditPagination.totalItems);
      return `${start} - ${end}`;
    },
    activeSprint() {
      return this.sprints.find(s => s.id === this.activeSprintId) || null;
    },
    otherSprints() {
      if (!this.activeSprintId) return [];
      return this.sprints.filter(s => s.id !== this.activeSprintId);
    },
    filteredSourceTasks() {
      if (this.portForm.filterMode === "failed_incomplete") {
        return this.portSourceTasks.filter(t => t.status !== "passed");
      }
      return this.portSourceTasks;
    },
    isAllPortSelected() {
      if (this.filteredSourceTasks.length === 0) return false;
      return this.filteredSourceTasks.every(t => this.portForm.selectedTaskIds.includes(t.id));
    },
    selectedPortCount() {
      return this.portForm.selectedTaskIds.length;
    },
    filteredCustomSourceTasks() {
      if (this.newSprint.custom_source === "library") {
        return this.rawTests || [];
      }
      return this.customSourceTasks || [];
    },
    isAllCustomSelected() {
      if (this.filteredCustomSourceTasks.length === 0) return false;
      return this.filteredCustomSourceTasks.every(t => this.newSprint.custom_test_ids.includes(t.id));
    },
    selectedCustomCount() {
      return this.newSprint.custom_test_ids.length;
    },
    totalTests() {
      if (this.activeTab === "sprints" && this.activeSprint) {
        // Exclude carried over (moved) tasks from active sprint scope
        return this.activeSprintTests.filter(t => t.status !== "moved").length;
      }
      return this.testGroups.reduce((acc, g) => acc + g.tests.length, 0);
    },
    movedTests() {
      if (this.activeTab === "sprints" && this.activeSprint) {
        return this.activeSprintTests.filter(t => t.status === "moved").length;
      }
      return 0;
    },
    checkedTests() {
      if (this.activeTab === "sprints" && this.activeSprint) {
        return this.activeSprintTests.filter(t => t.checked).length;
      }
      return this.testGroups.reduce(
        (acc, g) => acc + g.tests.filter((t) => t.checked).length,
        0
      );
    },
    passedCount() {
      if (this.activeTab === "sprints" && this.activeSprint) {
        return this.activeSprintTests.filter(t => t.status === "passed").length;
      }
      return this.testGroups.reduce(
        (acc, g) => acc + g.tests.filter((t) => t.status === "passed").length,
        0
      );
    },
    inProgressCount() {
      if (this.activeTab === "sprints" && this.activeSprint) {
        return this.activeSprintTests.filter(t => t.status === "in_progress").length;
      }
      return this.testGroups.reduce(
        (acc, g) => acc + g.tests.filter((t) => t.status === "in_progress").length,
        0
      );
    },
    failedCount() {
      if (this.activeTab === "sprints" && this.activeSprint) {
        return this.activeSprintTests.filter(t => t.status === "failed").length;
      }
      return this.testGroups.reduce(
        (acc, g) => acc + g.tests.filter((t) => t.status === "failed").length,
        0
      );
    },
    notStartedCount() {
      if (this.activeTab === "sprints" && this.activeSprint) {
        return this.activeSprintTests.filter(t => t.status === "not_started").length;
      }
      return this.testGroups.reduce(
        (acc, g) => acc + g.tests.filter((t) => t.status === "not_started").length,
        0
      );
    },
    overallPassedPercentage() {
      if (this.totalTests === 0) return 0;
      return (this.passedCount / this.totalTests) * 100;
    },
    formattedCurrentDate() {
      const today = new Date();
      return today.toLocaleDateString("en-AU", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    filteredGroups() {
      const sourceGroups = this.activeTab === "sprints" && this.activeSprint
        ? this.groupTests(this.activeSprintTests)
        : this.testGroups;

      return sourceGroups.map((group) => {
        const tests = group.tests.filter((test) => {
          // Live search match
          const query = this.searchQuery.toLowerCase().trim();
           const testDesc = (test.test || test.test_description || "").toLowerCase();
           const matchQuery =
             !query ||
             testDesc.includes(query) ||
             test.id.toString().includes(query) ||
             (test.task_code && test.task_code.toLowerCase().includes(query)) ||
             (test.sheet_id && test.sheet_id.toLowerCase().includes(query)) ||
             (test.sheetId && test.sheetId.toLowerCase().includes(query)) ||
             (test.cell_range && test.cell_range.toLowerCase().includes(query)) ||
             (test.cellRange && test.cellRange.toLowerCase().includes(query));

          // Tester assignee filter match
          const matchTester =
            !this.filterTester ||
            (test.tester && test.tester.toLowerCase().includes(this.filterTester.toLowerCase()));

          // Status filter match
          const matchStatus = !this.filterStatus || test.status === this.filterStatus;

          // Checked state match
          let matchChecked = true;
          if (this.filterChecked === "checked") {
            matchChecked = test.checked;
          } else if (this.filterChecked === "unchecked") {
            matchChecked = !test.checked;
          }

          // Sprint filter match
          const matchSprint =
            this.activeTab === "sprints" ||
            !this.filterSprint ||
            (test.sprint_number && test.sprint_number.trim() === this.filterSprint.trim());

          // Assigned to Me filter match
          let matchAssignedToMe = true;
          if (this.filterAssignedToMe) {
            const me = this.myFullName.toLowerCase();
            const email = (this.currentUser?.email || "").toLowerCase();
            const testersLower = (test.tester || "").toLowerCase();
            
            const firstName = (this.currentUser?.first_name || "").toLowerCase();
            const lastName = (this.currentUser?.last_name || "").toLowerCase();
            
            matchAssignedToMe = 
              (me && testersLower.includes(me)) ||
              (firstName && testersLower.includes(firstName)) ||
              (lastName && testersLower.includes(lastName)) ||
              (email && testersLower.includes(email));
          }

          return (
            matchQuery &&
            matchTester &&
            matchStatus &&
            matchChecked &&
            matchSprint &&
            matchAssignedToMe
          );
        });

        return {
          ...group,
          tests,
        };
      }).filter((g) => g.tests.length > 0);
    },
    listTestersFiltered() {
      return this.listTesters.filter(t => t !== "Unassigned");
    },
    availableSprints() {
      if (this.activeTab === "sprints") {
        return this.sprints.map(s => s.name);
      }
      const sprints = new Set();
      this.testGroups.forEach(group => {
        group.tests.forEach(test => {
          if (test.sprint_number && test.sprint_number.trim()) {
            sprints.add(test.sprint_number.trim());
          }
        });
      });
      return Array.from(sprints).sort((a, b) => {
        const numA = parseFloat(a);
        const numB = parseFloat(b);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        return a.localeCompare(b);
      });
    },
    currentUser() {
      return this.$store.state.auth.currentUser;
    },
    myFullName() {
      if (!this.currentUser) return "";
      return `${this.currentUser.first_name || ''} ${this.currentUser.last_name || ''}`.trim();
    },
  },

  watch: {
    passedCount: {
      handler() {
        this.renderDonutChart();
      },
      deep: true,
    },
    totalTests: {
      handler() {
        this.renderDonutChart();
      },
      deep: true,
    },
    auditSortBy() {
      this.auditPagination.page = 1;
      this.fetchAuditTrail();
    },
    "auditPagination.pageSize"() {
      this.auditPagination.page = 1;
      this.fetchAuditTrail();
    },
  },

  methods: {
    async fetchProjects() {
      this.isLoading = true;
      try {
        const res = await this.$axios.get("/regression/projects");
        if (res.data && res.data.success) {
          this.projects = res.data.projects || [];
        } else {
          this.$toast.error(res.data?.message || "Failed to fetch projects");
        }
      } catch (err) {
        console.error(err);
        this.$toast.error("Error loading regression projects.");
      } finally {
        this.isLoading = false;
      }
    },

    async fetchTests() {
      this.isLoading = true;
      try {
        const res = await this.$axios.get(`/regression/tests?project_id=${this.activeProjectId}`);
        if (res.data && res.data.success) {
          this.rawTests = res.data.tests || [];
          this.testGroups = this.groupTests(this.rawTests);
          
          // Keep collapse state expanded by default
          this.testGroups.forEach((g) => {
            if (this.collapsedGroups[g.id] === undefined) {
              this.collapsedGroups[g.id] = false;
            }
          });
        } else {
          this.$toast.error(res.data?.message || "Failed to fetch tests");
        }
      } catch (err) {
        console.error(err);
        this.$toast.error("Error loading regression tests from server database.");
      } finally {
        this.isLoading = false;
        this.renderDonutChart();
      }
    },

    async fetchBDMs() {
      try {
        const res = await this.$axios.get("/vertex-bdm");
        if (res.data && res.data.success && res.data.bdms) {
          this.availableBDMs = res.data.bdms;
          const bdmNames = res.data.bdms.map(b => `${b.first_name} ${b.last_name}`.trim());
          this.listTesters = [...bdmNames, "Unassigned"];
          this.availableTesters = [...bdmNames];
        }
      } catch (err) {
        console.error("Error loading BDMs:", err);
      }
    },

    backToDashboard() {
      this.currentView = "dashboard";
      this.fetchProjects(); // reload to get recalculated statistics
    },

    closeModalSafe(modalId) {
      const modalEl = document.getElementById(modalId);
      if (modalEl) {
        // Try triggering Close/Cancel button first (super robust, zero JS dependencies)
        const closeBtn = modalEl.querySelector('[data-bs-dismiss="modal"]');
        if (closeBtn) {
          closeBtn.click();
        } else {
          try {
            const bootstrapObj = (window.bootstrap && window.bootstrap.Modal) ? window.bootstrap : { Modal: BootstrapModal };
            if (bootstrapObj && bootstrapObj.Modal) {
              const modalInstance = bootstrapObj.Modal.getInstance(modalEl) || new bootstrapObj.Modal(modalEl);
              modalInstance.hide();
            }
          } catch (e) {
            console.error("Error closing modal programmatically:", e);
          }
        }
      }
      // Guarantee background overlays are cleaned up and page scrolling is restored
      setTimeout(() => {
        const backdrops = document.querySelectorAll(".modal-backdrop");
        backdrops.forEach((el) => el.remove());
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      }, 150);
    },
    
    generateNextSprintName(name) {
      const prefix = (this.activeProject?.prefix || "SP").trim();
      const currentYearStr = new Date().getFullYear().toString().slice(-2); // e.g., "26"
      
      let nextNum = "01";
      if (name) {
        const match = name.match(/(\d+)\s*$/);
        if (match && match[1]) {
          const parsed = parseInt(match[1], 10);
          if (!isNaN(parsed)) {
            const paddingLength = Math.max(match[1].length, 2);
            nextNum = (parsed + 1).toString().padStart(paddingLength, '0');
          }
        }
      }
      return `${prefix}${currentYearStr}.${nextNum}`;
    },

    openModalSafe(modalId) {
      const modalEl = document.getElementById(modalId);
      if (modalEl) {
        try {
          const bootstrapObj = (window.bootstrap && window.bootstrap.Modal) ? window.bootstrap : { Modal: BootstrapModal };
          if (bootstrapObj && bootstrapObj.Modal) {
            const modalInstance = bootstrapObj.Modal.getInstance(modalEl) || new bootstrapObj.Modal(modalEl);
            modalInstance.show();
          }
        } catch (e) {
          console.error("Error opening modal programmatically:", e);
        }
      }
    },

    async selectProjectView(proj) {
      this.activeProjectId = proj.id;
      this.currentView = "project";
      this.activeTab = "sprints";
      this.auditPagination.page = 1;
      await Promise.all([
        this.fetchSprints(),
        this.fetchTests()
      ]);
    },

    async selectProject(proj) {
      this.activeProjectId = proj.id;
      this.activeTab = "sprints";
      this.auditPagination.page = 1;
      await Promise.all([
        this.fetchSprints(),
        this.fetchTests()
      ]);
    },

    async deleteProject(proj) {
      this.showConfirmModal({
        title: "Delete Regression Project?",
        message: `Are you sure you want to delete the project "${proj.name}"?\nThis will permanently delete all of its associated test cases and cannot be undone.`,
        icon: "fa-trash-can text-danger",
        confirmBtnText: "Delete Project",
        confirmBtnClass: "btn-danger text-white",
        onConfirm: async () => {
          try {
            this.isLoading = true;
            const res = await this.$axios.delete(`/regression/projects/${proj.id}`);
            if (res.data && res.data.success) {
              this.$toast.success("Project and all associated tests deleted successfully!");
              if (this.activeProjectId === proj.id) {
                this.activeProjectId = 1;
                this.currentView = "dashboard";
              }
              await this.fetchProjects();
            } else {
              this.$toast.error(res.data?.message || "Failed to delete project");
            }
          } catch (err) {
            console.error(err);
            this.$toast.error("Error deleting project.");
          } finally {
            this.isLoading = false;
          }
        }
      });
    },

    hexToRgba(hex, alpha) {
      if (!hex) return `rgba(245, 158, 11, ${alpha})`;
      hex = hex.replace("#", "");
      if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
      }
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },

    isUrl(str) {
      if (!str) return false;
      return str.startsWith("http://") || str.startsWith("https://");
    },

    onIconFileChange(event, type) {
      const file = event.target.files[0];
      if (!file) return;

      if (!file.type.startsWith("image/") && file.type !== "image/svg+xml") {
        this.$toast.warning("Please upload image files only.");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const rawResult = e.target.result;
        const base64Data = rawResult.split(",")[1] || rawResult;
        if (type === "create") {
          this.newProject.encoded_icon = base64Data;
          this.newProject.icon_filename = file.name;
          this.newProject.preview_icon = rawResult;
        } else {
          this.editProject.encoded_icon = base64Data;
          this.editProject.icon_filename = file.name;
          this.editProject.preview_icon = rawResult;
        }
      };
      reader.readAsDataURL(file);
    },

    openCreateProjectModal() {
      this.newProject = { name: "", description: "", prefix: "", jira_board_link: "", icon: "fa-folder", color: "#f59e0b", encoded_icon: "", icon_filename: "", preview_icon: "" };
    },

    async saveNewProject() {
      if (!this.newProject.name.trim()) {
        this.$toast.warning("Project Name is required.");
        return;
      }
      try {
        this.isLoading = true;
        const res = await this.$axios.post("/regression/projects", this.newProject);
        if (res.data && res.data.success) {
          this.$toast.success("New project created successfully!");
          this.closeModalSafe("createProjectModal");
          this.activeProjectId = res.data.project.id;
          this.currentView = "project";
          this.activeTab = "sprints";
          await Promise.all([
            this.fetchProjects(),
            this.fetchSprints(),
            this.fetchTests()
          ]);
        } else {
          this.$toast.error(res.data?.message || "Failed to create project");
        }
      } catch (err) {
        console.error(err);
        const errMsg = err.response?.data?.message || "Error creating project.";
        this.$toast.error(errMsg);
      } finally {
        this.isLoading = false;
      }
    },

    openEditProjectModal() {
      if (!this.activeProject) return;
      this.editProject = {
        id: this.activeProject.id,
        name: this.activeProject.name || "",
        description: this.activeProject.description || "",
        prefix: this.activeProject.prefix || "",
        jira_board_link: this.activeProject.jira_board_link || "",
        icon: this.activeProject.icon || "fa-folder",
        color: this.activeProject.color || "#f59e0b",
        encoded_icon: "",
        icon_filename: "",
        preview_icon: "",
      };
    },

    openAndShowEditProjectModal(proj) {
      if (!proj) return;
      this.editProject = {
        id: proj.id,
        name: proj.name || "",
        description: proj.description || "",
        prefix: proj.prefix || "",
        jira_board_link: proj.jira_board_link || "",
        icon: proj.icon || "fa-folder",
        color: proj.color || "#f59e0b",
        encoded_icon: "",
        icon_filename: "",
        preview_icon: "",
      };
      this.openModalSafe("editProjectModal");
    },

    showConfirmModal({ title, message, icon, confirmBtnText, confirmBtnClass, onConfirm }) {
      this.confirmModal.title = title || "Confirm Action";
      this.confirmModal.message = message || "Are you sure you want to proceed?";
      this.confirmModal.icon = icon || "fa-triangle-exclamation text-warning";
      this.confirmModal.confirmBtnText = confirmBtnText || "Confirm";
      this.confirmModal.confirmBtnClass = confirmBtnClass || "btn-info text-white";
      this.confirmModal.onConfirm = onConfirm;
      
      this.openModalSafe("confirmModal");
    },

    handleConfirmModalAction() {
      if (typeof this.confirmModal.onConfirm === "function") {
        this.confirmModal.onConfirm();
      }
      this.closeModalSafe("confirmModal");
    },

    async saveEditProject() {
      if (!this.editProject.name.trim()) {
        this.$toast.warning("Project Name is required.");
        return;
      }
      try {
        this.isLoading = true;
        const res = await this.$axios.post(`/regression/projects/update/${this.editProject.id}`, this.editProject);
        if (res.data && res.data.success) {
          this.$toast.success("Project updated successfully!");
          this.closeModalSafe("editProjectModal");
          await this.fetchProjects();
        } else {
          this.$toast.error(res.data?.message || "Failed to update project");
        }
      } catch (err) {
        console.error(err);
        const errMsg = err.response?.data?.message || "Error updating project.";
        this.$toast.error(errMsg);
      } finally {
        this.isLoading = false;
      }
    },

    openCreateSprintModal() {
      const prefix = (this.activeProject?.prefix || "SP").trim();
      const currentYearStr = new Date().getFullYear().toString().slice(-2); // e.g., "26"
      
      let suggestedName = "";
      if (this.sprints && this.sprints.length > 0) {
        // Robustly find latest sprint by max ID to handle any API sorting order
        const latestSprint = [...this.sprints].reduce((prev, current) => (prev.id > current.id) ? prev : current);
        if (latestSprint && latestSprint.name) {
          suggestedName = this.generateNextSprintName(latestSprint.name);
        }
      }

      if (!suggestedName) {
        suggestedName = `${prefix}${currentYearStr}.01`;
      }

      this.newSprint = {
        name: suggestedName,
        populate_source: "library",
        prev_sprint_id: null,
        start_immediately: false,
        custom_source: "library",
        custom_test_ids: [],
      };
    },

    onCustomSourceChange() {
      this.newSprint.custom_test_ids = [];
      this.customSourceTasks = [];
      this.newSprint.prev_sprint_id = null;
      if (this.newSprint.custom_source === "library") {
        this.newSprint.custom_test_ids = this.rawTests.map(t => t.id);
      }
    },

    async onCustomPrevSprintChange() {
      if (!this.newSprint.prev_sprint_id) {
        this.customSourceTasks = [];
        this.newSprint.custom_test_ids = [];
        return;
      }
      try {
        this.isLoadingCustomSourceTasks = true;
        this.customSourceTasks = [];
        this.newSprint.custom_test_ids = [];
        
        const res = await this.$axios.get(`/regression/sprints/${this.newSprint.prev_sprint_id}/tests`);
        if (res.data && res.data.success) {
          this.customSourceTasks = (res.data.tests || []).map(t => ({
            ...t,
            test: t.test || t.test_description,
          }));
          this.newSprint.custom_test_ids = this.customSourceTasks.map(t => t.id);
        }
      } catch (err) {
        console.error("Error fetching custom source sprint tests:", err);
        this.$toast.error("Failed to load custom source sprint tasks.");
      } finally {
        this.isLoadingCustomSourceTasks = false;
      }
    },

    toggleCustomSelectAll() {
      if (this.isAllCustomSelected) {
        this.newSprint.custom_test_ids = [];
      } else {
        this.newSprint.custom_test_ids = this.filteredCustomSourceTasks.map(t => t.id);
      }
    },

    openPortTasksModal() {
      this.portForm = {
        sourceSprintId: null,
        filterMode: "all",
        resetTester: false,
        selectedTaskIds: [],
      };
      this.portSourceTasks = [];
    },

    async onPortSourceSprintChange() {
      if (!this.portForm.sourceSprintId) {
        this.portSourceTasks = [];
        this.portForm.selectedTaskIds = [];
        return;
      }
      try {
        this.isLoadingSourceTasks = true;
        this.portSourceTasks = [];
        this.portForm.selectedTaskIds = [];
        
        const res = await this.$axios.get(`/regression/sprints/${this.portForm.sourceSprintId}/tests`);
        if (res.data && res.data.success) {
          this.portSourceTasks = (res.data.tests || []).map(t => ({
            ...t,
            test: t.test || t.test_description,
            checked: !!t.checked,
          }));
          // Default: Select all tasks initially
          this.applyPortFilter();
        }
      } catch (err) {
        console.error("Error fetching source sprint tests for porting:", err);
        this.$toast.error("Failed to load source sprint tasks.");
      } finally {
        this.isLoadingSourceTasks = false;
      }
    },

    applyPortFilter() {
      // Automatically check all tasks matching the current filter
      this.portForm.selectedTaskIds = this.filteredSourceTasks.map(t => t.id);
    },

    togglePortSelectAll() {
      if (this.isAllPortSelected) {
        this.portForm.selectedTaskIds = [];
      } else {
        this.portForm.selectedTaskIds = this.filteredSourceTasks.map(t => t.id);
      }
    },

    async executePortTasks() {
      if (!this.portForm.sourceSprintId) {
        this.$toast.warning("Please select a source sprint.");
        return;
      }
      if (this.portForm.selectedTaskIds.length === 0) {
        this.$toast.warning("Please select at least one task to port.");
        return;
      }

      try {
        this.isPorting = true;
        const payload = {
          target_sprint_id: this.activeSprintId,
          source_sprint_id: this.portForm.sourceSprintId,
          source_test_ids: this.portForm.selectedTaskIds,
          reset_tester: this.portForm.resetTester,
        };

        const res = await this.$axios.post("/regression/sprints/tests/port", payload);
        if (res.data && res.data.success) {
          this.$toast.success(res.data.message || "Tasks successfully ported!");
          this.closeModalSafe("portTasksModal");
          // Refresh current sprint tasks and statistics
          await this.fetchSprintTests(this.activeSprintId);
        } else {
          this.$toast.error(res.data?.message || "Failed to port tasks.");
        }
      } catch (err) {
        console.error("Error executing porting of sprint tasks:", err);
        this.$toast.error("An error occurred while porting tasks.");
      } finally {
        this.isPorting = false;
      }
    },

    openAddTestModal() {
      this.isCreatingNewGroup = this.testGroups.length === 0;
      this.newTest = {
        group_name: "",
        test: "",
        spreadsheet_link: "",
        sheet_id: "",
        cell_range: "",
        status: "not_started",
        tester: "Unassigned",
        sprint_number: "",
        add_to_library: true,
        jira_id: "",
      };
      this.newTestTesters = ["Unassigned"];
    },

    triggerCSVFileInput() {
      this.$refs.csvFileInput.click();
    },

    handleCSVFileSelected(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.csvSelectedFile = file;
      this.csvFileName = file.name;
      
      // Open the Bootstrap modal natively
      this.openModalSafe("csvConfirmModal");

      // Clear file input value
      event.target.value = "";
    },

    async confirmCSVImport() {
      if (!this.csvSelectedFile) return;

      this.closeModalSafe("csvConfirmModal");
      this.isLoading = true;

      try {
        const formData = new FormData();
        formData.append("file", this.csvSelectedFile);
        formData.append("project_id", this.activeProjectId);

        const res = await this.$axios.post("/regression/tests/import", formData);

        if (res.data && res.data.success) {
          const data = res.data;
          this.csvImportReceipt = {
            total_rows: data.total_rows || 0,
            imported: data.imported || 0,
            skipped_duplicates: data.skipped_duplicates || 0,
          };
          
          // Open the Success Receipt Modal natively
          setTimeout(() => {
            this.openModalSafe("csvReceiptModal");
          }, 300); // Small timeout to allow confirm modal to slide out smoothly
          
          this.$toast.success("CSV Import completed!");
          await this.fetchTests();
        } else {
          this.$toast.error(res.data?.message || "Failed to import CSV");
        }
      } catch (err) {
        console.error(err);
        const errMsg = err.response?.data?.message || "Error importing CSV file.";
        this.$toast.error(errMsg);
      } finally {
        this.isLoading = false;
        this.csvSelectedFile = null;
      }
    },

    cancelCSVImport() {
      this.closeModalSafe("csvConfirmModal");
      this.csvSelectedFile = null;
      this.csvFileName = "";
    },

    openEditTestModal(test) {
      this.isEditingNewGroup = false;
      this.editTest = {
        id: test.id,
        group_name: test.group_name || "",
        test: test.test || test.test_description || "",
        spreadsheet_link: test.spreadsheet_link || test.spreadsheetLink || "",
        sheet_id: test.sheet_id || test.sheetId || "",
        cell_range: test.cell_range || test.cellRange || "",
        sprint_number: test.sprint_number || "",
        jira_id: test.jira_id || test.jiraId || "",
      };
    },

    async saveEditTest() {
      if (!this.editTest.group_name.trim() || !this.editTest.test.trim()) {
        this.$toast.warning("Category (Group Name) and Test Description are required.");
        return;
      }
      try {
        this.isLoading = true;
        const payload = {
          group_name: this.editTest.group_name,
          test: this.editTest.test,
          spreadsheet_link: this.editTest.spreadsheet_link,
          sheet_id: this.editTest.sheet_id,
          cell_range: this.editTest.cell_range,
          sprint_number: this.editTest.sprint_number,
          jira_id: this.editTest.jira_id,
        };
        const endpoint = this.activeTab === "sprints"
          ? `/regression/sprints/tests/update/${this.editTest.id}`
          : `/regression/tests/update/${this.editTest.id}`;

        const res = await this.$axios.post(endpoint, payload);
        if (res.data && res.data.success) {
          this.$toast.success("Test case updated successfully!");
          this.closeModalSafe("editTestModal");

          if (this.activeTab === "sprints") {
            await this.fetchSprintTests(this.activeSprintId);
          } else {
            await this.fetchTests();
          }
        } else {
          this.$toast.error(res.data?.message || "Failed to update test case");
        }
      } catch (err) {
        console.error(err);
        this.$toast.error("Error updating test case.");
      } finally {
        this.isLoading = false;
      }
    },

    getJiraIssueUrl(jiraId) {
      if (!jiraId) return "#";
      const base = (this.activeProject?.jira_board_link || "").trim();
      if (!base) return "#";

      // If the JIRA link ends with '=' or contains triggers like 'selectedIssue=' or has query marks, append directly
      if (base.includes("selectedIssue") || base.endsWith("=") || base.includes("?")) {
        return `${base}${jiraId.trim()}`;
      }

      try {
        const url = new URL(base);
        return `${url.origin}/browse/${jiraId.trim()}`;
      } catch (e) {
        if (base.endsWith("/")) {
          return `${base}browse/${jiraId.trim()}`;
        }
        return `${base}/browse/${jiraId.trim()}`;
      }
    },

    toggleNewTestTester(tester) {
      if (tester === 'Unassigned') {
        this.newTestTesters = ['Unassigned'];
      } else {
        this.newTestTesters = this.newTestTesters.filter(t => t !== 'Unassigned');
        if (this.newTestTesters.includes(tester)) {
          this.newTestTesters = this.newTestTesters.filter(t => t !== tester);
        } else {
          this.newTestTesters.push(tester);
        }
        if (this.newTestTesters.length === 0) {
          this.newTestTesters = ['Unassigned'];
        }
      }
    },

    async saveNewTest() {
      if (!this.newTest.group_name.trim() || !this.newTest.test.trim()) {
        this.$toast.warning("Category (Group Name) and Test Description are required.");
        return;
      }
      try {
        this.isLoading = true;
        this.newTest.tester = this.newTestTesters.join(", ");
        const payload = {
          project_id: this.activeProjectId,
          ...this.newTest,
        };
        if (this.activeTab === "sprints" && this.activeSprintId) {
          payload.sprint_id = parseInt(this.activeSprintId);
        }
        const res = await this.$axios.post("/regression/tests", payload);
        if (res.data && res.data.success) {
          this.$toast.success("New test case added successfully!");
          this.closeModalSafe("addTestModal");

          if (this.activeTab === "sprints" && this.activeSprintId) {
            await this.fetchSprintTests(this.activeSprintId);
          }
          await this.fetchTests();
        } else {
          this.$toast.error(res.data?.message || "Failed to save test case");
        }
      } catch (err) {
        console.error(err);
        this.$toast.error("Error creating test case.");
      } finally {
        this.isLoading = false;
      }
    },

    async promoteSprintTaskToMaster(test) {
      try {
        this.isLoading = true;
        const res = await this.$axios.post(`/regression/sprints/tests/${test.id}/promote`);
        if (res.data && res.data.success) {
          this.$toast.success("Task successfully promoted and linked to Master Library!");
          
          if (this.activeSprintId) {
            await this.fetchSprintTests(this.activeSprintId);
          }
          await this.fetchTests();
        } else {
          this.$toast.error(res.data?.message || "Failed to promote task.");
        }
      } catch (err) {
        console.error(err);
        this.$toast.error("Error promoting task to master library.");
      } finally {
        this.isLoading = false;
      }
    },

    groupTests(flatTests) {
      const groupsMap = {};
      flatTests.forEach((test) => {
        const gName = test.group_name;
        if (!groupsMap[gName]) {
          groupsMap[gName] = {
            id: "group_" + Object.keys(groupsMap).length,
            name: gName,
            status: "Not Started",
            tester: test.group_tester,
            tests: [],
          };
        }
        groupsMap[gName].tests.push({
          ...test,
          id: test.id,
          test: test.test,
          link: test.link,
          spreadsheetLink: test.spreadsheet_link,
          sheetId: test.sheet_id,
          cellRange: test.cell_range,
          status: test.status,
          tester: test.tester,
          checked: test.checked === true || test.checked === 1,
          sprint_number: test.sprint_number,
        });
      });
      return Object.values(groupsMap);
    },

    jumpToSprint(sprintId) {
      if (!sprintId) return;
      const sprint = this.sprints.find(s => s.id === sprintId);
      if (sprint) {
        this.activeTab = "sprints";
        this.selectSprint(sprint);
        this.$toast.info(`Switched to Sprint Run: ${sprint.name}`);
      } else {
        this.$toast.error("Sprint run not found or access restricted.");
      }
    },

    jumpToMasterTest(testId, taskCode) {
      if (!testId) return;
      this.activeTab = "library";
      const displayId = taskCode || `#${testId}`;
      this.$toast.info(`Switched to Master Library. Looking for Test ${displayId}...`);
      this.$nextTick(() => {
        const element = document.getElementById(`testerDropLib_${testId}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          const row = element.closest("tr");
          if (row) {
            row.style.backgroundColor = "rgba(0, 160, 193, 0.15)";
            setTimeout(() => {
              row.style.transition = "background-color 1s ease";
              row.style.backgroundColor = "";
            }, 2500);
          }
        }
      });
    },

    isTesterSelected(test, testerName) {
      if (!test.tester) return testerName === "Unassigned";
      const selected = test.tester.split(",").map(s => s.trim());
      if (testerName === "Unassigned") {
        return selected.length === 0 || (selected.length === 1 && selected[0] === "Unassigned") || (selected.length === 1 && selected[0] === "");
      }
      return selected.includes(testerName);
    },

    toggleTester(test, testerName) {
      let selected = test.tester ? test.tester.split(",").map(s => s.trim()).filter(Boolean) : [];
      
      if (testerName === "Unassigned") {
        selected = ["Unassigned"];
      } else {
        selected = selected.filter(s => s !== "Unassigned");
        if (selected.includes(testerName)) {
          selected = selected.filter(s => s !== testerName);
        } else {
          selected.push(testerName);
        }
        if (selected.length === 0) {
          selected = ["Unassigned"];
        }
      }
      
      test.tester = selected.join(", ");
      this.saveTestState(test);
    },

    toggleTesterSprint(test, testerName) {
      let selected = test.tester ? test.tester.split(",").map(s => s.trim()).filter(Boolean) : [];
      
      if (testerName === "Unassigned") {
        selected = ["Unassigned"];
      } else {
        selected = selected.filter(s => s !== "Unassigned");
        if (selected.includes(testerName)) {
          selected = selected.filter(s => s !== testerName);
        } else {
          selected.push(testerName);
        }
        if (selected.length === 0) {
          selected = ["Unassigned"];
        }
      }
      
      test.tester = selected.join(", ");
      this.saveSprintTestState(test);
    },
 
     getInitials(name) {
       if (!name || name === 'Unassigned') return "?";
       const parts = name.trim().split(/\s+/);
       if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
       return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
     },
 
     getAvatarColor(name) {
       if (!name || name === 'Unassigned') return "#9CA3AF"; // Gray for unassigned
       // Deterministic color selection matching bright premium theme palette (Richie Golez is yellow)
       const colors = [
         "#EAB308", // Yellow (e.g. for Richie Golez)
         "#00A0C1", // Vertex Blue/Teal
         "#10B981", // Emerald
         "#8B5CF6", // Purple
         "#EC4899", // Pink
         "#3B82F6", // Blue
         "#F97316", // Orange
         "#06B6D4", // Cyan
       ];
       let hash = 0;
       for (let i = 0; i < name.length; i++) {
         hash = name.charCodeAt(i) + ((hash << 5) - hash);
       }
       const index = Math.abs(hash) % colors.length;
       return colors[index];
     },
 
     formatGroupTester(testerStr) {
       if (!testerStr) return "";
       // Split by slashes, commas, or semicolons
       const parts = testerStr.split(/[/,;]+/).map(s => s.trim()).filter(Boolean);
       // Deduplicate
       const uniqueParts = [...new Set(parts)];
       return uniqueParts.join(" / ");
     },

    quickSetStatus(test, newStatus) {
      test.status = newStatus;
      // Auto-check only on passed. Failed status remains unchecked so it can be rechecked.
      if (newStatus === "passed") {
        test.checked = true;
      } else if (newStatus === "failed") {
        test.checked = false;
      }
      this.saveTestState(test);
    },

    async saveTestState(test) {
      try {
        const payload = {
          status: test.status,
          tester: test.tester,
          checked: test.checked,
        };
        const res = await this.$axios.post(`/regression/tests/update/${test.id}`, payload);
        if (res.data && res.data.success) {
          this.$toast.success(`Test #${test.id} saved to DB!`);
          this.renderDonutChart();
        } else {
          this.$toast.error(res.data?.message || "Failed to update test record");
        }
      } catch (err) {
        console.error(err);
        this.$toast.error(`Error saving test #${test.id} to DB`);
      }
    },

    async resetToDefaults() {
      const projName = this.activeProject ? this.activeProject.name : "this project";
      this.showConfirmModal({
        title: "Reset to Defaults?",
        message: `Are you sure you want to restore "${projName}" to its defaults?\nFor Auth Regression, this restores the default 81 tests. For custom projects, this clears all tests.`,
        icon: "fa-arrow-rotate-left text-warning",
        confirmBtnText: "Reset Defaults",
        confirmBtnClass: "btn-warning text-white",
        onConfirm: async () => {
          this.isLoading = true;
          try {
            const res = await this.$axios.post(`/regression/tests/reset?project_id=${this.activeProjectId}`);
            if (res.data && res.data.success) {
              this.$toast.success("Database restored successfully!");
              await this.fetchTests();
            } else {
              this.$toast.error(res.data?.message || "Failed to reset project tests");
            }
          } catch (err) {
            console.error(err);
            this.$toast.error("Error resetting database project tests.");
          } finally {
            this.isLoading = false;
          }
        }
      });
    },

    toggleGroup(groupId) {
      this.collapsedGroups[groupId] = !this.collapsedGroups[groupId];
    },

    toggleAllCollapse(expand) {
      this.testGroups.forEach((g) => {
        this.collapsedGroups[g.id] = !expand;
      });
    },

    getPassedCountForGroup(group) {
      return group.tests.filter((t) => t.status === "passed").length;
    },

    getFailedCountForGroup(group) {
      return group.tests.filter((t) => t.status === "failed").length;
    },

    getInProgressCountForGroup(group) {
      return group.tests.filter((t) => t.status === "in_progress").length;
    },

    getNotStartedCountForGroup(group) {
      return group.tests.filter((t) => !t.status || t.status === "not_started").length;
    },

    getMovedCountForGroup(group) {
      return group.tests.filter((t) => t.status === "moved").length;
    },

    getCompletionPercentageForGroup(group) {
      if (!group.tests.length) return 0;
      const passed = this.getPassedCountForGroup(group);
      return Math.round((passed / group.tests.length) * 100);
    },

    getGroupStatus(group) {
      const tests = group.tests;
      if (tests.length === 0) return "Not Started";
      
      const allPassed = tests.every((t) => t.status === "passed");
      const anyFailed = tests.some((t) => t.status === "failed");
      const anyInProgress = tests.some((t) => t.status === "in_progress");
      const anyPassed = tests.some((t) => t.status === "passed");

      if (anyFailed) return "Failed";
      if (allPassed) return "Passed";
      if (anyInProgress || anyPassed) return "In Progress";
      return "Not Started";
    },

    getGroupStatusBadgeClass(group) {
      const status = this.getGroupStatus(group);
      if (status === "Passed") return "bg-success text-white";
      if (status === "Failed") return "bg-danger text-white";
      if (status === "In Progress") return "bg-warning text-dark";
      return "bg-secondary text-white";
    },

    getGroupProgressBarClass(group) {
      const status = this.getGroupStatus(group);
      if (status === "Passed") return "bg-success";
      if (status === "Failed") return "bg-danger";
      if (status === "In Progress") return "bg-warning";
      return "bg-secondary";
    },

    getStatusSelectClass(status) {
      if (status === "passed") return "border-success text-success fw-bold bg-success bg-opacity-10";
      if (status === "failed") return "border-danger text-danger fw-bold bg-danger bg-opacity-10";
      if (status === "in_progress") return "border-warning text-warning fw-bold bg-warning bg-opacity-10";
      if (status === "moved") return "border-purple text-purple fw-bold bg-purple-light";
      return "border-secondary text-secondary bg-light bg-opacity-50";
    },

    getStatusColor(status) {
      switch (status) {
        case "not_started":
          return "#6c757d"; // Gray
        case "in_progress":
          return "#ffc107"; // Warning Yellow
        case "failed":
          return "#dc3545"; // Red
        case "passed":
          return "#198754"; // Green
        case "moved":
          return "#6f42c1"; // Purple/Indigo
        default:
          return "#6c757d";
      }
    },

    getStatusBadgeClass(status) {
      switch (status) {
        case "not_started":
          return "bg-secondary text-white";
        case "in_progress":
          return "bg-warning text-dark";
        case "failed":
          return "bg-danger text-white";
        case "passed":
          return "bg-success text-white";
        case "moved":
          return "bg-purple text-white";
        default:
          return "bg-secondary text-white";
      }
    },

    // ── Drag & Drop test cases inside groups ──
    handleDragStart(event, groupId, testId) {
      this.draggedTestId = testId;
      this.draggedGroupId = groupId;
      event.dataTransfer.effectAllowed = "move";
    },

    async handleDrop(event, targetGroupId, targetTestId) {
      if (this.draggedGroupId !== targetGroupId) {
        this.$toast.warning("Test cases cannot be moved across categories.");
        return;
      }
      const group = this.testGroups.find((g) => g.id === targetGroupId);
      if (group) {
        const tests = group.tests;
        const dragIndex = tests.findIndex((t) => t.id === this.draggedTestId);
        const dropIndex = tests.findIndex((t) => t.id === targetTestId);
        if (dragIndex !== -1 && dropIndex !== -1) {
          const temp = tests[dragIndex];
          tests.splice(dragIndex, 1);
          tests.splice(dropIndex, 0, temp);
          
          try {
            await this.$axios.post(`/regression/tests/update/${this.draggedTestId}`, { order_index: dropIndex });
            await this.$axios.post(`/regression/tests/update/${targetTestId}`, { order_index: dragIndex });
            this.$toast.success("Reordered successfully!");
          } catch (e) {
            console.error(e);
          }
          this.renderDonutChart();
        }
      }
    },

    // ── Canvas-based Donut Progress Chart ──
    renderDonutChart() {
      this.$nextTick(() => {
        const canvas = document.getElementById("progressDonut");
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, 120, 120);

        const x = canvas.width / 2;
        const y = canvas.height / 2;
        const radius = 48;
        const lineWidth = 12;

        const total = this.totalTests || 1;
        const passedArc = (this.passedCount / total) * 2 * Math.PI;
        const inProgArc = (this.inProgressCount / total) * 2 * Math.PI;
        const failedArc = (this.failedCount / total) * 2 * Math.PI;
        const notStartArc = (this.notStartedCount / total) * 2 * Math.PI;

        let startAngle = -0.5 * Math.PI;

        // Render Background base track
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, 2 * Math.PI);
        ctx.strokeStyle = "#f3f4f6";
        ctx.lineWidth = lineWidth;
        ctx.stroke();

        // Passed Segment (Success Emerald)
        if (passedArc > 0) {
          ctx.beginPath();
          ctx.arc(x, y, radius, startAngle, startAngle + passedArc);
          ctx.strokeStyle = "#10b981"; 
          ctx.lineWidth = lineWidth;
          ctx.lineCap = "round";
          ctx.stroke();
          startAngle += passedArc;
        }

        // In Progress Segment (Warning Orange)
        if (inProgArc > 0) {
          ctx.beginPath();
          ctx.arc(x, y, radius, startAngle, startAngle + inProgArc);
          ctx.strokeStyle = "#f59e0b"; 
          ctx.lineWidth = lineWidth;
          ctx.stroke();
          startAngle += inProgArc;
        }

        // Failed Segment (Danger Red)
        if (failedArc > 0) {
          ctx.beginPath();
          ctx.arc(x, y, radius, startAngle, startAngle + failedArc);
          ctx.strokeStyle = "#ef4444"; 
          ctx.lineWidth = lineWidth;
          ctx.stroke();
          startAngle += failedArc;
        }

        // Not Started Segment (Gray Secondary)
        if (notStartArc > 0) {
          ctx.beginPath();
          ctx.arc(x, y, radius, startAngle, startAngle + notStartArc);
          ctx.strokeStyle = "#9ca3af"; 
          ctx.lineWidth = lineWidth;
          ctx.stroke();
        }
      });
    },

    // ── Export Report Modal & Printing ──
    downloadPDF() {
      const modalEl = document.getElementById("reportPreviewModal");
      if (modalEl) {
        const modal = new window.bootstrap.Modal(modalEl);
        modal.show();
      }
    },

    printReport() {
      window.print();
    },

    exportCSV() {
      let csvContent = "data:text/csv;charset=utf-8,";
      csvContent += "Group,ID,Test Description,Spreadsheet Link,Sheet ID,Cell Range,Status,Tester,Checked\n";

      this.testGroups.forEach((group) => {
        group.tests.forEach((test) => {
          const cleanDesc = test.test.replace(/"/g, '""');
          csvContent += `"${group.name}",${test.id},"${cleanDesc}","${test.spreadsheetLink || ""}","${test.sheetId || ""}","${test.cellRange || ""}","${test.status}","${test.tester}","${test.checked ? "Yes" : "No"}\n`;
        });
      });

      const projNameClean = this.activeProject ? this.activeProject.name.toLowerCase().replace(/[^a-z0-9]/g, "_") : "regression_library";
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `${projNameClean}_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.$toast.success("CSV file exported successfully!");
    },

    handleBoardDragStart(event, test) {
      event.dataTransfer.setData("text/plain", test.id);
      event.dataTransfer.effectAllowed = "move";
    },

    async handleBoardDrop(event, targetStatus) {
      const testId = event.dataTransfer.getData("text/plain");
      if (!testId) return;

      let targetTest = null;
      const groupsToSearch = this.activeTab === "sprints"
        ? this.groupTests(this.activeSprintTests)
        : this.testGroups;

      for (const group of groupsToSearch) {
        const found = group.tests.find(t => String(t.id) === String(testId));
        if (found) {
          targetTest = found;
          break;
        }
      }

      if (targetTest && targetTest.status !== targetStatus) {
        const prevStatus = targetTest.status;
        const prevChecked = targetTest.checked;
        
        targetTest.status = targetStatus;
        if (targetStatus === "passed") {
          targetTest.checked = true;
        } else if (targetStatus === "failed") {
          targetTest.checked = false;
        }

        try {
          const payload = {
            status: targetStatus,
            checked: targetTest.checked,
          };
          const endpoint = this.activeTab === "sprints"
            ? `/regression/sprints/tests/update/${testId}`
            : `/regression/tests/update/${testId}`;

          const res = await this.$axios.post(endpoint, payload);
          if (res.data && res.data.success) {
            this.$toast.success(`Moved to ${targetStatus === 'not_started' ? 'Not Started' : targetStatus === 'in_progress' ? 'In Progress' : targetStatus.toUpperCase()}!`);
            if (this.activeTab === "sprints") {
              await this.fetchSprintTests(this.activeSprintId);
            } else {
              await this.fetchTests();
            }
          } else {
            targetTest.status = prevStatus;
            targetTest.checked = prevChecked;
            this.$toast.error(res.data?.message || "Failed to update status");
          }
        } catch (err) {
          console.error(err);
          targetTest.status = prevStatus;
          targetTest.checked = prevChecked;
          this.$toast.error("Error updating status");
        }
      }
    },

    formatSprintTag(sprintNumber) {
      if (!sprintNumber || !sprintNumber.trim()) return "";
      
      const prefix = this.activeProject?.prefix || "";
      if (!prefix) return `Sprint ${sprintNumber}`;
      
      const currentYearShort = new Date().getFullYear().toString().slice(-2);
      
      // If already fully formatted, just return uppercase
      if (sprintNumber.toLowerCase().startsWith(prefix.toLowerCase())) {
        return sprintNumber.toUpperCase();
      }
      
      let num = sprintNumber.trim();
      if (num.length === 1 && !isNaN(num)) {
        num = "0" + num;
      }
      
      return `${prefix.toUpperCase()}${currentYearShort}.${num}`;
    },

    getBoardColumnTests(status) {
      const flat = [];
      this.filteredGroups.forEach(group => {
        group.tests.forEach(test => {
          if (test.status === status) {
            flat.push({
              ...test,
              group_name: group.name,
            });
          }
        });
      });
      return flat;
    },

    // ── Sprint & Agile Traceability Methods ──
    async fetchSprints() {
      try {
        this.isLoadingSprints = true;
        const res = await this.$axios.get(`/regression/sprints?project_id=${this.activeProjectId}`);
        if (res.data && res.data.success) {
          this.sprints = res.data.sprints || [];
          let selectedS = this.sprints.find(s => s.status === 'active');
          if (!selectedS && this.sprints.length > 0) {
            selectedS = this.sprints[0];
          }
          if (selectedS) {
            this.activeSprintId = selectedS.id;
            await this.fetchSprintTests(selectedS.id);
          } else {
            this.activeSprintId = null;
            this.activeSprintTests = [];
          }
        }
      } catch (err) {
        console.error("Error fetching sprints:", err);
      } finally {
        this.isLoadingSprints = false;
        this.renderDonutChart();
      }
    },

    async fetchSprintTests(sprintId) {
      try {
        this.isLoadingSprintTests = true;
        const res = await this.$axios.get(`/regression/sprints/${sprintId}/tests`);
        if (res.data && res.data.success) {
          // Normalize API fields for checked and group_name
          this.activeSprintTests = (res.data.tests || []).map(t => ({
            ...t,
            test: t.test || t.test_description,
            spreadsheetLink: t.spreadsheet_link || t.spreadsheetLink,
            sheetId: t.sheet_id !== undefined ? t.sheet_id : t.sheetId,
            cellRange: t.cell_range || t.cellRange,
            checked: !!t.checked,
            group_name: t.group_name || "General Tests",
          }));
        }
      } catch (err) {
        console.error("Error fetching sprint tests:", err);
      } finally {
        this.isLoadingSprintTests = false;
        this.renderDonutChart();
      }
    },

    async fetchAuditTrail() {
      try {
        this.isLoadingAudit = true;
        let sort = "created_at";
        let order = "desc";
        if (this.auditSortBy === "dateCreated_asc") {
          sort = "created_at";
          order = "asc";
        }
        const res = await this.$axios.get(`/regression/sprints/audit?project_id=${this.activeProjectId}&page=${this.auditPagination.page}&pageSize=${this.auditPagination.pageSize}&sort=${sort}&order=${order}`);
        if (res.data && res.data.success) {
          this.auditTrail = res.data.audit || res.data.logs || [];
          this.auditPagination.totalItems = res.data.total_items || 0;
          this.auditPagination.totalPages = res.data.pages ? Math.ceil(this.auditPagination.totalItems / this.auditPagination.pageSize) : 1;
        }
      } catch (err) {
        console.error("Error fetching audit trail:", err);
      } finally {
        this.isLoadingAudit = false;
      }
    },

    async previousAuditPage() {
      if (this.auditPagination.page > 1) {
        this.auditPagination.page--;
        await this.fetchAuditTrail();
      }
    },

    async nextAuditPage() {
      if (this.auditPagination.page < this.auditPagination.totalPages) {
        this.auditPagination.page++;
        await this.fetchAuditTrail();
      }
    },

    getAuditActionClass(action) {
      switch (action) {
        case "created":
          return "bg-success text-white";
        case "added":
          return "bg-info text-white";
        case "status_changed":
          return "bg-warning text-dark";
        case "moved":
          return "bg-purple text-white";
        case "promoted":
          return "bg-primary text-white";
        case "ported":
          return "bg-dark text-white";
        default:
          return "bg-secondary text-white";
      }
    },

    getAuditActionIcon(action) {
      switch (action) {
        case "created":
          return "fa-circle-plus";
        case "added":
          return "fa-folder-plus";
        case "status_changed":
          return "fa-pen-to-square";
        case "moved":
          return "fa-person-running";
        case "promoted":
          return "fa-circle-up";
        case "ported":
          return "fa-truck-ramp-box";
        default:
          return "fa-clock-rotate-left";
      }
    },

    getAuditActionBadgeClass(action) {
      switch (action) {
        case "created":
          return "bg-success bg-opacity-10 text-success border border-success border-opacity-20";
        case "added":
          return "bg-info bg-opacity-10 text-info border border-info border-opacity-20";
        case "status_changed":
          return "bg-warning bg-opacity-10 text-warning border border-warning border-opacity-20";
        case "moved":
          return "origin-sprint-badge";
        case "promoted":
          return "bg-primary bg-opacity-10 text-primary border border-primary border-opacity-20";
        case "ported":
          return "bg-dark bg-opacity-10 text-dark border border-dark border-opacity-20";
        default:
          return "bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-20";
      }
    },

    formatTimeAgo(dateVal) {
      if (!dateVal) return "N/A";
      const date = new Date(dateVal);
      if (isNaN(date.getTime())) return "N/A";
      const now = new Date();
      const seconds = Math.floor((now - date) / 1000);
      if (seconds < 5) return "just now";
      if (seconds < 60) return `${seconds}s ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      if (days < 30) return `${days}d ago`;
      return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    },

    formatLogNotes(notes) {
      if (!notes) return "";
      let formatted = notes;
      // Replace raw database status keys with clean, spaced, user-friendly labels
      formatted = formatted.replace(/\bnot_started\b/g, "not started");
      formatted = formatted.replace(/\bin_progress\b/g, "in progress");
      formatted = formatted.replace(/\bpassed\b/g, "passed");
      formatted = formatted.replace(/\bfailed\b/g, "failed");
      formatted = formatted.replace(/\bmoved\b/g, "moved");
      return formatted;
    },

    async switchTab(tab) {
      this.activeTab = tab;
      if (tab === "sprints") {
        await this.fetchSprints();
      } else if (tab === "library") {
        await this.fetchTests();
      } else if (tab === "audit") {
        await this.fetchAuditTrail();
      }
      this.$nextTick(() => {
        this.renderDonutChart();
      });
    },

    async selectSprint(s) {
      this.activeSprintId = s.id;
      await this.fetchSprintTests(s.id);
    },

    async saveNewSprint() {
      if (!this.newSprint.name.trim()) {
        this.$toast.warning("Sprint Name is required.");
        return;
      }
      try {
        const payload = {
          project_id: this.activeProjectId,
          name: this.newSprint.name.trim(),
          populate_source: this.newSprint.populate_source,
          prev_sprint_id: (this.newSprint.populate_source === 'custom' && this.newSprint.custom_source === 'previous') || (this.newSprint.populate_source !== 'library' && this.newSprint.populate_source !== 'none' && this.newSprint.populate_source !== 'custom') ? parseInt(this.newSprint.prev_sprint_id) : 0,
          status: this.newSprint.start_immediately ? "active" : "planned",
          custom_source: this.newSprint.populate_source === 'custom' ? this.newSprint.custom_source : "",
          custom_test_ids: this.newSprint.populate_source === 'custom' ? this.newSprint.custom_test_ids : [],
        };
        const res = await this.$axios.post("/regression/sprints", payload);
        if (res.data && res.data.success) {
          if (payload.status === "planned") {
            this.$toast.success("New future planned sprint run defined and saved successfully!");
          } else {
            this.$toast.success("New active sprint run defined and started successfully!");
          }
          this.closeModalSafe("createSprintModal");
          this.newSprint = { name: "", populate_source: "library", prev_sprint_id: null, start_immediately: false, custom_source: "library", custom_test_ids: [] };
          await this.fetchSprints();
        } else {
          this.$toast.error(res.data?.message || "Failed to define sprint");
        }
      } catch (err) {
        console.error(err);
        const errMsg = err.response?.data?.message || "Error defining sprint.";
        this.$toast.error(errMsg);
      }
    },

    async completeActiveSprint() {
      if (!this.activeSprintId) return;
      this.showConfirmModal({
        title: "Complete Sprint Run?",
        message: `Are you sure you want to mark "${this.activeSprint?.name}" as completed?\nThis will freeze its current execution progress state and log details to the historical audit trace.`,
        icon: "fa-circle-check text-success",
        confirmBtnText: "Complete Sprint",
        confirmBtnClass: "btn-info text-white",
        onConfirm: async () => {
          try {
            const res = await this.$axios.post(`/regression/sprints/${this.activeSprintId}/complete`);
            if (res.data && res.data.success) {
              this.$toast.success("Sprint run completed and archived successfully!");
              await this.fetchSprints();
            } else {
              this.$toast.error(res.data?.message || "Failed to complete sprint");
            }
          } catch (err) {
            console.error(err);
            const errMsg = err.response?.data?.message || "Error completing sprint.";
            this.$toast.error(errMsg);
          }
        }
      });
    },

    async startPlannedSprint() {
      if (!this.activeSprintId) return;
      const isCompleted = this.activeSprint?.status === 'completed';
      const title = isCompleted ? "Reactivate Sprint Run?" : "Activate Sprint Run?";
      const message = isCompleted
        ? `Are you sure you want to unlock and reactivate "${this.activeSprint?.name}"?\nThis will allow live edits to its tests.`
        : `Are you sure you want to activate and start "${this.activeSprint?.name}"?\nThis will set its status to active and enable live execution metrics.`;
      const icon = isCompleted ? "fa-lock text-warning" : "fa-circle-play text-info";
      const confirmBtnText = isCompleted ? "Reactivate Sprint" : "Activate Sprint";

      this.showConfirmModal({
        title,
        message,
        icon,
        confirmBtnText,
        confirmBtnClass: "btn-info text-white",
        onConfirm: async () => {
          try {
            const res = await this.$axios.post(`/regression/sprints/${this.activeSprintId}/start`);
            if (res.data && res.data.success) {
              const successMsg = isCompleted
                ? "Sprint run unlocked and reactivated successfully!"
                : "Sprint run started and activated successfully!";
              this.$toast.success(successMsg);
              await this.fetchSprints();
            } else {
              this.$toast.error(res.data?.message || "Failed to activate sprint");
            }
          } catch (err) {
            console.error(err);
            const errMsg = err.response?.data?.message || "Error activating sprint.";
            this.$toast.error(errMsg);
          }
        }
      });
    },

    async saveSprintTestState(test) {
      try {
        const payload = {
          status: test.status,
          checked: !!test.checked,
        };
        const endpoint = this.activeTab === "sprints"
          ? `/regression/sprints/tests/update/${test.id}`
          : `/regression/tests/update/${test.id}`;

        const res = await this.$axios.post(endpoint, payload);
        if (res.data && res.data.success) {
          this.$toast.success("State updated successfully!");
          if (this.activeTab === "sprints") {
            await this.fetchSprintTests(this.activeSprintId);
          } else {
            await this.fetchTests();
          }
        } else {
          this.$toast.error(res.data?.message || "Failed to update test state");
        }
      } catch (err) {
        console.error(err);
        this.$toast.error("Error updating test state.");
      }
    },

    async quickSetSprintStatus(test, status) {
      const prevStatus = test.status;
      const prevChecked = test.checked;
      
      test.status = status;
      if (status === "passed") {
        test.checked = true;
      } else if (status === "failed") {
        test.checked = false;
      }

      try {
        const payload = {
          status: status,
          checked: test.checked,
        };
        const endpoint = this.activeTab === "sprints"
          ? `/regression/sprints/tests/update/${test.id}`
          : `/regression/tests/update/${test.id}`;

        const res = await this.$axios.post(endpoint, payload);
        if (res.data && res.data.success) {
          this.$toast.success(`Updated status to ${status.toUpperCase()}!`);
          if (this.activeTab === "sprints") {
            await this.fetchSprintTests(this.activeSprintId);
          } else {
            await this.fetchTests();
          }
        } else {
          test.status = prevStatus;
          test.checked = prevChecked;
          this.$toast.error(res.data?.message || "Failed to quick update status");
        }
      } catch (err) {
        console.error(err);
        test.status = prevStatus;
        test.checked = prevChecked;
        this.$toast.error("Error quick updating status.");
      }
    },

    openMoveTaskModal(test) {
      let suggestedName = "Sprint 01";
      if (this.sprints && this.sprints.length > 0) {
        // Robustly find latest sprint by max ID to handle any API sorting order
        const latestSprint = [...this.sprints].reduce((prev, current) => (prev.id > current.id) ? prev : current);
        if (latestSprint && latestSprint.name) {
          suggestedName = this.generateNextSprintName(latestSprint.name);
        }
      }

      this.moveTaskForm = {
        sprintTestId: test.id,
        testId: test.test_id || test.id,
        targetSprintId: null,
        newSprintName: suggestedName,
        createNew: false,
      };
    },

    async executeMoveTask() {
      if (this.moveTaskForm.createNew && !this.moveTaskForm.newSprintName.trim()) {
        this.$toast.warning("New Sprint Name is required.");
        return;
      }
      try {
        const payload = {
          project_id: this.activeProjectId,
          target_sprint_id: this.moveTaskForm.createNew ? 0 : parseInt(this.moveTaskForm.targetSprintId),
          new_sprint_name: this.moveTaskForm.createNew ? this.moveTaskForm.newSprintName.trim() : "",
        };
        const res = await this.$axios.post(`/regression/sprints/tests/${this.moveTaskForm.sprintTestId}/move`, payload);
        if (res.data && res.data.success) {
          this.$toast.success("Task moved/carried-over successfully!");
          this.closeModalSafe("moveSprintTestModal");
          await this.fetchSprints();
        } else {
          this.$toast.error(res.data?.message || "Failed to carry over task");
        }
      } catch (err) {
        console.error(err);
        const errMsg = err.response?.data?.message || "Error carrying over task.";
        this.$toast.error(errMsg);
      }
    },
  },
};
</script>

<style scoped>
.filterTitle {
   font-weight: 500;
   color: #435666;
}

.filterOptions {
   font-weight: 500;
   color: #00A0C1;
}

.bordered-select {
   border: 1px solid #dee2e6 !important;
   border-radius: 8px !important;
   padding-top: 0.25rem;
   padding-bottom: 0.25rem;
   padding-left: 0.75rem;
   height: 35px;
   width: auto;
}

.pagination {
   display: inline-flex;
   align-items: center;
   border: 1px solid #dee2e6;
   border-radius: 8px;
   background-color: #fff;
   overflow: hidden;
   height: 35px;
}

.pagination-label {
   font-size: 0.9rem;
   color: #6c757d;
   font-weight: 500;
   padding: 0 8px;
   white-space: nowrap;
}

.pagination-label strong {
   color: #343a40;
}

.pagination-arrow {
   background-color: transparent;
   border: none;
   cursor: pointer;
   color: #00A0C1;
   font-size: 1rem;
   padding: 8px 12px;
   transition: background-color 0.2s ease;
}

.pagination-arrow:hover:not(:disabled) {
   background-color: #f1f3f5;
}

.pagination-arrow:disabled {
   color: #ced4da;
   cursor: not-allowed;
}

.form-check-input:checked, .custom-teal-checkbox:checked {
  background-color: #008298 !important;
  border-color: #008298 !important;
}
.form-check-input:focus, .custom-teal-checkbox:focus {
  border-color: #008298 !important;
  box-shadow: 0 0 0 0.25rem rgba(0, 130, 152, 0.25) !important;
}

/* Premium Project Settings Buttons Hover & Transitions */
.project-settings-btn-main {
  color: #4b5563 !important;
  border-color: #d1d5db !important;
  border-radius: 20px !important;
  transition: all 0.25s ease !important;
}
.project-settings-btn-main:hover {
  color: #ffffff !important;
  border-color: #00A0C1 !important;
  background-color: #00A0C1 !important;
  box-shadow: 0 4px 12px rgba(0, 160, 193, 0.2) !important;
}
.project-settings-icon {
  color: #6b7280;
  transition: transform 0.35s ease, color 0.2s ease;
}
.project-settings-btn-main:hover .project-settings-icon {
  transform: rotate(60deg);
  color: #ffffff !important;
}

.card-settings-btn {
  color: #9ca3af !important;
  transition: color 0.2s ease, transform 0.35s ease !important;
}
.card-settings-btn:hover {
  color: #00A0C1 !important;
  transform: rotate(60deg);
}

.edit-proj-dropdown-btn {
  color: #9ca3af !important;
  transition: color 0.2s ease, transform 0.35s ease !important;
}
.edit-proj-dropdown-btn:hover {
  color: #00A0C1 !important;
  transform: rotate(60deg);
}

/* -------------------------------------------------------------
   SPARK UNIFIED HEADERS & BREADCRUMBS (Matches Spark's Premium Card Header)
   ------------------------------------------------------------- */
.ms-page-header {
  margin-bottom: 24px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #ffffff 0%, #f4f9fb 100%);
  border: 1px solid rgba(0, 130, 152, 0.15);
  border-left: 5px solid #008298;
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(0, 78, 90, 0.05);
}
.id-link-cell {
  cursor: pointer !important;
  transition: all 0.15s ease !important;
}
.id-link-cell:hover {
  color: #008298 !important;
  background-color: rgba(0, 130, 152, 0.05) !important;
  text-decoration: underline !important;
}
.ms-page-header__content {
  display: flex;
  align-items: center;
  gap: 16px;
}
.ms-page-header__icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(0, 130, 152, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  color: #008298;
}
.ms-page-header__breadcrumb {
  font-size: 0.76rem;
  color: #557d84;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 4px;
}
.ms-breadcrumb-sep {
  margin: 0 6px;
  color: rgba(0, 130, 152, 0.3);
}
.ms-page-header__title {
  font-size: 1.45rem;
  font-weight: 800;
  color: #00363f;
  margin: 0;
  letter-spacing: -0.3px;
}

.back-btn {
  background-color: #ffffff !important;
  border-color: rgba(0, 130, 152, 0.3) !important;
  color: #008298 !important;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1) !important;
}
.back-btn:hover, .back-btn:focus, .back-btn:active {
  background: linear-gradient(135deg, #008298, #005c6b) !important;
  border-color: #005c6b !important;
  color: #ffffff !important;
  box-shadow: 0 4px 12px rgba(0, 130, 152, 0.2) !important;
}

.regression-library-page {
  font-family: "Inter", sans-serif;
  color: #435666;
}

/* ── Dashboard Cards Styles ── */
.project-card {
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.22s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.22s ease;
  border: 1px solid rgba(0, 0, 0, 0.02) !important;
}
.project-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 25px rgba(67, 86, 102, 0.08) !important;
}

.project-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
}
.project-icon-wrapper.auth-theme {
  background-color: rgba(16, 185, 129, 0.08);
  color: #10b981;
}
.project-icon-wrapper.custom-theme {
  background-color: rgba(245, 158, 11, 0.08);
  color: #f59e0b;
}

.card-project-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #2c3e50;
  transition: color 0.15s ease;
}
.project-card:hover .card-project-title {
  color: #0dcaf0;
}

.text-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-delete-btn {
  color: #ced4da;
  font-size: 0.95rem;
  transition: color 0.15s ease, transform 0.15s ease;
}
.card-delete-btn:hover {
  color: #ef4444 !important;
  transform: scale(1.1);
}

.card-action-link {
  transition: color 0.15s ease;
}
.card-arrow {
  transition: transform 0.2s ease;
}
.project-card:hover .card-arrow {
  transform: translateX(5px);
}

/* ── Details Panel Styles ── */
.icon-circle {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.stat-title {
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #566f83;
}

.passed-border {
  border-left: 4px solid #10b981 !important;
}
.in-progress-border {
  border-left: 4px solid #f59e0b !important;
}
.failed-border {
  border-left: 4px solid #ef4444 !important;
}
.not-started-border {
  border-left: 4px solid #9ca3af !important;
}

.stat-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
}

/* Donut text styling */
.donut-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.donut-percentage {
  font-size: 1.4rem;
  font-weight: 800;
  color: #1f2937;
  line-height: 1;
}
.donut-sub {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  color: #9ca3af;
  margin-top: 2px;
}

/* Category grid and table */
.group-container {
  overflow: hidden;
  transition: box-shadow 0.2s ease;
}
.group-container:hover {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.04) !important;
}

.group-header {
  transition: background-color 0.15s ease;
}
.group-header:hover {
  background-color: #fafbfc !important;
}

.drag-handle {
  opacity: 0.3;
  transition: opacity 0.15s ease;
}
tr:hover .drag-handle {
  opacity: 0.8;
}

.ms-checkbox {
  width: 17px;
  height: 17px;
  cursor: pointer;
  border-color: #cbd5e1;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}
.ms-checkbox:checked {
  background-color: #00A0C1 !important;
  border-color: #00A0C1 !important;
}
.ms-checkbox:focus {
  border-color: #00A0C1 !important;
  box-shadow: 0 0 0 0.25rem rgba(0, 160, 193, 0.2) !important;
}

.ms-checkbox-sm {
  width: 15px;
  height: 15px;
  cursor: pointer;
  border-color: #cbd5e1;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}
.ms-checkbox-sm:checked {
  background-color: #00A0C1 !important;
  border-color: #00A0C1 !important;
}
.ms-checkbox-sm:focus {
  border-color: #00A0C1 !important;
  box-shadow: 0 0 0 0.25rem rgba(0, 160, 193, 0.2) !important;
}

/* Custom Vertex Switch Overrides */
.vertex-switch:checked {
  background-color: #00A0C1 !important;
  border-color: #00A0C1 !important;
}
.vertex-switch:focus {
  border-color: #00A0C1 !important;
  box-shadow: 0 0 0 0.25rem rgba(0, 160, 193, 0.2) !important;
}

/* Premium Vertex Blue Checkboxes inside the Assignees dropdown */
.tester-check-input {
  cursor: pointer;
  border-color: #cbd5e1;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}
.tester-check-input:checked {
  background-color: #00A0C1 !important;
  border-color: #00A0C1 !important;
}
.tester-check-input:focus {
  border-color: #00A0C1 !important;
  box-shadow: 0 0 0 0.25rem rgba(0, 160, 193, 0.2) !important;
}
.cursor-pointer {
  cursor: pointer;
}

.status-select {
  cursor: pointer;
  transition: all 0.15s ease;
}

.row-checked {
  background-color: #f8fafc !important; /* Premium subtle light background tint for completed test cases instead of opacity */
}

/* Establish a base stacking context on all rows by default so descendants (like badges with z-index) can never escape and paint on top of other rows */
tr {
  position: relative;
  z-index: 1;
}

/* Elevate the z-index of rows when they contain open dropdowns or are focused/hovered to avoid clipping */
tr:focus-within,
tr:hover {
  position: relative;
  z-index: 10 !important;
}

/* Ensure Popper dropdowns are drawn on top of adjacent cells/rows and are 100% solid white (opaque) to prevent bleed-through */
.dropdown-menu {
  z-index: 1060 !important;
  background-color: #ffffff !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12) !important;
}

.delete-proj-btn {
  opacity: 0.4;
  transition: opacity 0.15s ease;
}
.delete-proj-btn:hover {
  opacity: 1 !important;
}

/* Quick action status buttons */
.quick-status-btn {
  width: 28px;
  height: 28px;
  border-radius: 50% !important; /* Perfectly round circular buttons */
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  border: 1px solid #dee2e6;
  background: white;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0;
  cursor: pointer;
}
.quick-status-btn:hover {
  transform: scale(1.12); /* Interactive hover scaling pop-up effect */
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12) !important;
}
.pass-btn.outline-pass {
  color: #10b981;
  border-color: rgba(16, 185, 129, 0.25);
}
.pass-btn.outline-pass:hover {
  background-color: #10b981;
  color: white;
  border-color: #10b981;
}
.pass-btn.active-pass {
  background-color: #10b981;
  color: white;
  border-color: #10b981;
}

.fail-btn.outline-fail {
  color: #ef4444;
  border-color: rgba(239, 68, 68, 0.25);
}
.fail-btn.outline-fail:hover {
  background-color: #ef4444;
  color: white;
  border-color: #ef4444;
}
.fail-btn.active-fail {
  background-color: #ef4444;
  color: white;
  border-color: #ef4444;
}

.move-btn.outline-move {
  color: #8b5cf6;
  border-color: rgba(139, 92, 246, 0.25);
}
.move-btn.outline-move:hover {
  background-color: #8b5cf6;
  color: white;
  border-color: #8b5cf6;
}

.edit-row-btn {
  opacity: 0.6;
  transition: all 0.15s ease;
}
tr:hover .edit-row-btn {
  opacity: 0.85;
}
tr:hover .edit-row-btn:hover {
  opacity: 1 !important;
  transform: scale(1.2);
}

@media print {
  body * {
    visibility: hidden;
  }
  #pdfReportContent, #pdfReportContent * {
    visibility: visible;
  }
  #pdfReportContent {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
  }
}

/* Agile Workflow Styles */
.nav-tab-btn {
  border-radius: 6px;
  border: 1px solid transparent;
  background-color: transparent;
  color: #4b5563;
  transition: all 0.2s ease;
  font-weight: 600;
  font-size: 13.5px;
}
.nav-tab-btn:hover {
  background-color: #f3f4f6;
  color: #1f2937;
}
.nav-tab-btn.active {
  background-color: #008298;
  color: white !important;
  border-color: #008298;
  box-shadow: 0 4px 12px rgba(0, 130, 152, 0.2);
}

.timeline-container {
  position: relative;
  padding-left: 32px;
  border-left: 2px solid rgba(0, 130, 152, 0.15);
  margin-left: 12px;
}
.timeline-item {
  position: relative;
  margin-bottom: 28px;
}
.timeline-item:last-child {
  margin-bottom: 0;
}
.timeline-icon-bullet {
  position: absolute;
  left: -43px;
  top: 2px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: white;
  border: 2px solid #008298;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  box-shadow: 0 0 0 4px rgba(0, 130, 152, 0.05);
}
.timeline-icon-bullet i {
  font-size: 10px;
  color: #008298;
}
.timeline-icon-bullet.bg-success {
  border-color: #10b981 !important;
}
.timeline-icon-bullet.bg-success i {
  color: #10b981 !important;
}
.timeline-icon-bullet.bg-danger {
  border-color: #ef4444 !important;
}
.timeline-icon-bullet.bg-danger i {
  color: #ef4444 !important;
}
.timeline-icon-bullet.bg-warning {
  border-color: #f59e0b !important;
}
.timeline-icon-bullet.bg-warning i {
  color: #f59e0b !important;
}

.cursor-pointer {
  cursor: pointer;
}

/* Custom Purple Utilities for Sprint Origin Link Badge & Audit Log */
.bg-purple {
  background-color: #8b5cf6 !important;
}
.bg-purple-light {
  background-color: rgba(139, 92, 246, 0.1) !important;
}
.border-purple {
  border-color: rgba(139, 92, 246, 0.25) !important;
}
.text-purple {
  color: #8b5cf6 !important;
}
.origin-sprint-badge {
  background-color: rgba(139, 92, 246, 0.1) !important;
  color: #8b5cf6 !important;
  border: 1px solid rgba(139, 92, 246, 0.15) !important;
}

/* Premium Project & Sprint Switcher Dropdown Item Hover/Active Styles with Vertex Blue */
.dropdown-item {
  transition: background-color 0.2s ease, color 0.2s ease !important;
}
.dropdown-item:hover, .dropdown-item:focus, .dropdown-item:active {
  background-color: #00A0C1 !important;
  color: #ffffff !important;
}
.dropdown-item:hover *, .dropdown-item:focus *, .dropdown-item:active * {
  color: #ffffff !important;
}
</style>
